// ============================================================================
// Resilient add_to_cart tracking — workaround for the Shopify Web Pixels gap.
//
// Since 2026-06-16, Shopify's web_pixels_manager intermittently fails to publish
// `product_added_to_cart` (~⅔ of real adds never broadcast — an intermittent
// race in Shopify's pixel sandbox, reproduced and confirmed not to be caused by
// our theme code). The drop is masked in the overnight UTC hours (low traffic)
// but is unambiguous across every US-daytime hour from ~13:00 UTC June 16 on.
// Every downstream pixel listens for that one broadcast, so PostHog, GA4 and
// Meta all lost ~⅔ of add_to_cart — including the paid-ads optimization signal.
// Root cause is on Shopify's side and out of our control, so instead of relying
// on their broadcast we emit the event ourselves from the main page, where it
// fires 100% of the time.
//
// Approach: fire add_to_cart directly to the three sinks whose SDKs live on the
// main page — PostHog, GA4 (gtag), Meta (fbq) — on every successful /cart/add.js.
// The reliable hook on THIS store is jQuery's `ajaxSuccess` (the theme's adds go
// through $.ajax): it fires at jQuery's API layer, which works even though
// Global-e proxies /cart/* at the network layer — a raw fetch/XMLHttpRequest
// wrapper never sees the cart adds here (verified). A fetch wrapper is kept as a
// best-effort secondary for the few fetch-based add paths. Value/currency come
// from the add response.
//
// TEMPORARY measure — intentional limitations (accepted, see PR):
//   * TikTok is NOT covered: its `ttq` SDK lives only in Shopify's pixel
//     sandbox, unreachable from the main page. It stays on the (broken) Shopify
//     pixel until that's fixed.
//   * No dedup: for the ~⅓ of adds where Shopify's pixel DOES still fire, that
//     add is counted twice in PostHog/GA4/Meta. This is a deliberate trade —
//     recovering the lost ~⅔ far outweighs a double-count on ~⅓. Remove this
//     block once Shopify fixes the publish gap.
//   * Fetch-based add paths behind Global-e's /cart proxy won't fire the
//     secondary hook; those flows redirect straight to checkout, where
//     checkout_started still fires, so the missing add_to_cart is non-critical.
// ============================================================================
(function () {
  if (window.__audigoAtcEmitInstalled) { return; }
  window.__audigoAtcEmitInstalled = true;

  var ADD_URL_RE = /\/cart\/add(\.js)?(\?|$)/;

  function emitAddToCart(addResponse) {
    try {
      // /cart/add.js returns either a single line item or { items: [...] }.
      var items = addResponse && addResponse.items
        ? addResponse.items
        : (addResponse && addResponse.id ? [addResponse] : []);

      items.forEach(function (it) {
        if (!it) return;
        var qty = it.quantity || 1;
        var cents = typeof it.final_price === 'number' ? it.final_price : it.price;
        var unitPrice = (cents || 0) / 100;            // cents -> major units
        var value = unitPrice * qty;
        var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
        var variantId = String(it.variant_id || it.id || '');
        var title = it.product_title || it.title || '';
        var variantTitle = it.variant_title || '';

        // Meta (Facebook/Instagram)
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'AddToCart', {
            content_ids: [variantId],
            content_type: 'product',
            content_name: title,
            value: value,
            currency: currency
          });
        }
        // GA4 / Google Ads (gtag)
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'add_to_cart', {
            currency: currency,
            value: value,
            items: [{ item_id: variantId, item_name: title, price: unitPrice, quantity: qty }]
          });
        }
        // PostHog (tagged so it's distinguishable from the shopify-web-pixel events)
        if (window.posthog && typeof window.posthog.capture === 'function') {
          window.posthog.capture('add_to_cart', {
            product_title: title,
            variant_title: variantTitle,
            variant_id: variantId,
            price: unitPrice,
            currency: currency,
            quantity: qty,
            source: 'theme_resilient_emit'
          });
        }
      });
    } catch (e) {
      // Never let tracking break the add-to-cart flow.
      if (window.console && console.warn) { console.warn('[atc-emit] failed', e); }
    }
  }

  // Primary hook: jQuery ajaxSuccess (covers all $.ajax add paths, Global-e-proof).
  function wireAjaxSuccess() {
    if (!window.jQuery) { return false; }
    window.jQuery(document).ajaxSuccess(function (event, xhr, settings) {
      try {
        if (settings && ADD_URL_RE.test(settings.url || '')) {
          emitAddToCart(JSON.parse(xhr.responseText));
        }
      } catch (e) { /* malformed response — ignore */ }
    });
    return true;
  }
  if (!wireAjaxSuccess()) {
    var _jqWait = setInterval(function () { if (wireAjaxSuccess()) { clearInterval(_jqWait); } }, 200);
    setTimeout(function () { clearInterval(_jqWait); }, 15000);
  }

  // Secondary best-effort: fetch-based adds not proxied by Global-e.
  // ($.ajax adds use XHR, so they never reach this branch — no double emit.)
  if (window.fetch) {
    var _origFetch = window.fetch;
    window.fetch = function (resource, init) {
      var url = (resource && resource.url) ? resource.url : String(resource);
      var p = _origFetch.apply(this, arguments);
      if (ADD_URL_RE.test(url)) {
        p.then(function (res) {
          if (res && res.ok) {
            try { res.clone().json().then(emitAddToCart).catch(function () {}); } catch (e) {}
          }
        }).catch(function () {});
      }
      return p;
    };
  }
})();

// VWO Event Tracking for Checkout Buttons
(function() {
  var VWO_EVENT_NAME = 'checkoutButtonClicked';

  // VWO reference code initialization (ensures VWO.event works correctly)
  window.VWO = window.VWO || [];
  window.VWO.event = window.VWO.event || function () {
    window.VWO.push(['event'].concat([].slice.call(arguments)));
  };

  function trackCheckoutButtonClick() {
    try {
      // Call VWO event exactly as documented
      window.VWO.event(VWO_EVENT_NAME);
      console.log('[VWO] Event fired:', VWO_EVENT_NAME);
    } catch (err) {
      console.error('[VWO] trackEvent error:', err);
    }
  }

  // Use delegated events from document - can't be removed by .off('click') on buttons
  $(document).ready(function() {
    $(document).on('click.vwoEventTracking', '[id^="VWO-checkout-button-"]', function() {
      trackCheckoutButtonClick();
    });
  });
})();

// ============================================================
// VWO → GA4 (via GTM dataLayer): Push experiment/variation data
// so GTM can attach it as custom dimensions to all GA4 events.
// ============================================================
(function() {
  window.dataLayer = window.dataLayer || [];
  var COOKIE_NAME = '_vwo_ds';  // "vwo dataLayer sync"
  var COOKIE_DAYS = 7;
  var POLL_INTERVAL = 500;      // ms between checks for _vwo_exp
  var MAX_POLLS = 20;           // give VWO 10s to load

  // --- cookie helpers ---
  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie = name + '=' + encodeURIComponent(value) +
      ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }
  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  // --- build experiment payload from _vwo_exp ---
  function buildVwoPayload() {
    var exps = window._vwo_exp;
    if (!exps) return null;
    var experiments = [];
    for (var cId in exps) {
      if (!exps.hasOwnProperty(cId)) continue;
      var exp = exps[cId];
      if (!exp || !exp.combination_chosen) continue;
      experiments.push({
        id: cId,
        name: String(exp.name || ''),
        variation: String(exp.combination_chosen)
      });
    }
    return experiments.length ? experiments : null;
  }

  // --- push to dataLayer ---
  function pushToDataLayer(experiments) {
    // Combined string for GA4 custom dimension "VWO Experiments"
    // Format: "campaignId:variationId,campaignId:variationId"
    var combined = experiments.map(function(e) { return e.id + ':' + e.variation; }).join(',');
    experiments.forEach(function(exp) {
      window.dataLayer.push({
        event: 'vwo_experiment_active',
        vwo_campaign_id: exp.id,
        vwo_campaign_name: exp.name,
        vwo_variation_id: exp.variation,
        vwo_experiments: combined
      });
    });
    console.log('[VWO→GA4] Pushed experiment data to dataLayer:', combined);
    registerToPosthog(experiments, combined);
  }

  // --- register VWO experiments as PostHog super properties ---
  // So every subsequent PostHog event (scroll-depth milestones, etc.)
  // carries the VWO variant, the same way the dataLayer carries it to
  // GA4. `vwo_experiments` is the combined breakdown dimension; each
  // `vwo_<campaignId>` is a per-experiment key for clean single-test
  // funnel breakdowns in PostHog.
  function registerToPosthog(experiments, combined) {
    if (typeof posthog === 'undefined' || typeof posthog.register !== 'function') return;
    var props = { vwo_experiments: combined };
    experiments.forEach(function(exp) {
      props['vwo_' + exp.id] = exp.variation;
    });
    try {
      posthog.register(props);
      console.log('[VWO→PostHog] Registered experiment super properties:', combined);
    } catch (e) { /* swallow */ }
  }

  // --- main: poll for VWO, persist, push ---
  function init() {
    var polls = 0;
    var timer = setInterval(function() {
      polls++;
      var payload = buildVwoPayload();
      if (payload) {
        clearInterval(timer);
        // Persist to cookie so it's available on checkout/thank-you pages
        // even if VWO doesn't run there
        setCookie(COOKIE_NAME, JSON.stringify(payload), COOKIE_DAYS);
        pushToDataLayer(payload);
        return;
      }
      if (polls >= MAX_POLLS) {
        clearInterval(timer);
        // VWO never loaded — try to restore from cookie
        restoreFromCookie();
      }
    }, POLL_INTERVAL);
  }

  function restoreFromCookie() {
    var raw = getCookie(COOKIE_NAME);
    if (!raw) return;
    try {
      var payload = JSON.parse(raw);
      if (Array.isArray(payload) && payload.length) {
        pushToDataLayer(payload);
        console.log('[VWO→GA4] Restored experiment data from cookie');
      }
    } catch (e) { /* corrupted cookie — ignore */ }
  }

  // On pages where VWO runs, poll for it. On all pages, ensure
  // dataLayer gets the data (from live _vwo_exp or cookie fallback).
  init();
})();

// ============================================================
// PostHog → GA4 (via GTM dataLayer): Push experiment/variation data
// so GTM can attach it as custom dimensions to all GA4 events.
// Mirrors the VWO → GA4 pattern above.
// ============================================================
(function() {
  window.dataLayer = window.dataLayer || [];
  var COOKIE_NAME = '_ph_exp';  // "posthog experiment dataLayer sync"
  var COOKIE_DAYS = 30;

  // --- cookie helpers ---
  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie = name + '=' + encodeURIComponent(value) +
      ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }
  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  // --- build experiment payload from PostHog feature flags ---
  function buildPosthogPayload() {
    if (typeof posthog === 'undefined' || typeof posthog.getFeatureFlag !== 'function') return null;
    var flags = posthog.featureFlags.getFlagVariants();
    if (!flags) return null;
    var experiments = [];
    for (var key in flags) {
      if (!flags.hasOwnProperty(key)) continue;
      var variant = flags[key];
      // Only include multivariate flags (experiments), not boolean feature flags
      if (typeof variant === 'string') {
        experiments.push({ key: key, variant: variant });
      }
    }
    return experiments.length ? experiments : null;
  }

  // --- push to dataLayer ---
  function pushToDataLayer(experiments) {
    // Combined string for GA4 custom dimension "PostHog Experiments"
    // Format: "flagKey:variant,flagKey:variant"
    var combined = experiments.map(function(e) { return e.key + ':' + e.variant; }).join(',');
    experiments.forEach(function(exp) {
      window.dataLayer.push({
        event: 'posthog_experiment_active',
        posthog_flag_key: exp.key,
        posthog_variant: exp.variant,
        posthog_experiments: combined
      });
    });
    console.log('[PostHog→GA4] Pushed experiment data to dataLayer:', combined);
  }

  function restoreFromCookie() {
    var raw = getCookie(COOKIE_NAME);
    if (!raw) return;
    try {
      var payload = JSON.parse(raw);
      if (Array.isArray(payload) && payload.length) {
        pushToDataLayer(payload);
        console.log('[PostHog→GA4] Restored experiment data from cookie');
      }
    } catch (e) { /* corrupted cookie — ignore */ }
  }

  // --- main: only record on experiment entry, restore on other pages ---
  function isExperimentEntry() {
    var params = new URLSearchParams(window.location.search);
    return params.has('exp');
  }

  function init() {
    if (isExperimentEntry() && typeof posthog !== 'undefined' && typeof posthog.onFeatureFlags === 'function') {
      // First visit via experiment link — capture flag and persist
      posthog.onFeatureFlags(function() {
        var payload = buildPosthogPayload();
        if (payload) {
          setCookie(COOKIE_NAME, JSON.stringify(payload), COOKIE_DAYS);
          pushToDataLayer(payload);
        }
      });
    } else {
      // All other pages — restore from cookie so GA4 can attribute conversions
      restoreFromCookie();
    }
  }

  init();
})();

// ============================================================
// Analytics identifiers → cart attributes
// Writes the six _ph_/_ga_/_meta_/_kl_/_hj_ keys onto the cart, fresh on
// each cart mutation. See docs/order-analytics-identifiers.md.
// ============================================================
(function() {
  // Cookie reader; escapes regex metachars and returns '' (not null) when absent.
  function readCookie(name) {
    var safe = name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1');
    var match = document.cookie.match(new RegExp('(?:^|; )' + safe + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  // PostHog distinct_id; falls back to the id theme.liquid bootstraps the SDK with
  // (Shopify's visitor id), then the Shopify visitor cookies.
  function getPostHogDistinctId() {
    try {
      if (window.posthog && typeof posthog.get_distinct_id === 'function') {
        var id = posthog.get_distinct_id();
        if (id) return String(id);
      }
    } catch (e) { /* SDK not ready */ }
    return window.__audigoDistinctId || readCookie('shopify_client_id') || readCookie('_shopify_y');
  }

  // GA4 client_id from _ga cookie (GA1.<depth>.<clientId>); drop the first
  // two dotted segments — clientId itself contains a dot.
  function getGAClientId() {
    var raw = readCookie('_ga');
    if (!raw) return '';
    var parts = raw.split('.');
    return parts.length >= 4 ? parts.slice(2).join('.') : '';
  }

  // Meta browser pixel ID.
  function getMetaFbp() {
    return readCookie('_fbp');
  }

  // Meta click ID. Use the _fbc cookie, else synthesize from a fbclid URL
  // param in Meta's format fb.<subdomainIndex>.<creationTimeMs>.<fbclid>
  // (covers a pixel that loads after first paint and misses writing _fbc).
  function getMetaFbc() {
    var fbc = readCookie('_fbc');
    if (fbc) return fbc;
    try {
      var fbclid = new URLSearchParams(window.location.search).get('fbclid');
      if (fbclid) return 'fb.1.' + Date.now() + '.' + fbclid;
    } catch (e) { /* no URLSearchParams / bad query */ }
    return '';
  }

  // Klaviyo profile ID from the __kla_id cookie (base64 JSON; NOT _kx).
  // Identified visitors expose $exchange_id; anonymous expose `cid`, itself a
  // base64-encoded UUID — decode to the raw id either way.
  function getKlaviyoId() {
    var raw = readCookie('__kla_id');
    if (!raw) return '';
    try {
      var data = JSON.parse(atob(raw));
      var explicit = data['$exchange_id'] || data['$external_id'] ||
        data['$email_id'] || data['$user_id'];
      if (explicit) return String(explicit);
      if (data.cid) {
        try { return atob(data.cid); } catch (e2) { return String(data.cid); }
      }
      return '';
    } catch (e) { return ''; }
  }

  // Hotjar user ID from the _hjSessionUser_<siteId> cookie (dynamic suffix;
  // value is base64 JSON with an `id`).
  function getHotjarUserId() {
    try {
      var match = document.cookie.match(/_hjSessionUser_\d+=([^;]+)/);
      if (!match) return '';
      var data = JSON.parse(atob(decodeURIComponent(match[1])));
      return data && data.id ? String(data.id) : '';
    } catch (e) { return ''; }
  }

  function buildIdentifierAttributes() {
    return {
      '_ph_distinct_id': getPostHogDistinctId(),
      '_ga_client_id': getGAClientId(),
      '_meta_fbp': getMetaFbp(),
      '_meta_fbc': getMetaFbc(),
      '_kl_id': getKlaviyoId(),
      '_hj_user_id': getHotjarUserId()
    };
  }

  // Writes happen ONLY at page load and page exit — never during the active
  // add/remove interactions in between. Any write issued mid-interaction puts a
  // concurrent /cart/update.js into the theme's delicately timed iOS
  // mic-consolidation / subscription / mic-removal sequences and clobbers their
  // line-item changes. Load and exit are the two moments guaranteed to be free
  // of an in-flight cart mutation, so confining writes to them is race-free.
  // The IDs are stable per session, so this fully captures them.
  var _cartTouched = false;  // does the cart have items worth writing to?

  // POST attributes to /cart/update.js. Shopify merges `attributes` (other cart
  // keys intact); an empty-string value is a delete, so inactive tools drop
  // their key. `keepalive` lets the exit write complete after the page unloads.
  // Returns the fetch promise so callers that need the write to commit before
  // navigating (see stampIdentifiersAwaitable) can await it.
  function writeIdentifierAttributes(keepalive) {
    try {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: buildIdentifierAttributes() }),
        keepalive: !!keepalive
      }).catch(function() { /* non-critical analytics write */ });
    } catch (e) { return Promise.resolve(); /* fetch unavailable */ }
  }

  // Awaitable stamp for the moment just before a checkout navigation, when the
  // cart is settled (no line-item mutation in flight). Resolves when the write
  // commits OR after a hard cap — so a caller can `await` this before redirecting
  // to /checkout to guarantee the attributes land, without ever blocking the
  // redirect on a slow/failed network. This is what closes the gap the page-exit
  // `pagehide` keepalive left: that write races the navigation and is lossy on
  // iOS Safari; an awaited pre-redirect write is deterministic.
  function stampIdentifiersAwaitable() {
    _cartTouched = true;
    var cap = new Promise(function(resolve) { setTimeout(resolve, 1500); });
    return Promise.race([writeIdentifierAttributes(false), cap]);
  }
  // Exposed for the direct-to-checkout flows (which clear the cart — and its
  // attributes — then redirect) and the checkout-button interception below.
  window.audigoStampIdentifiers = stampIdentifiersAwaitable;

  // Page exit (navigating to checkout / another page) — the cart is idle here,
  // so this can't race a mutation. Covers a fresh cart's first add when the
  // user adds then checks out without any intervening page load. pagehide only
  // (NOT visibilitychange) so a mid-session tab switch never triggers a write.
  function writeIdentifiersOnExit() {
    if (_cartTouched) writeIdentifierAttributes(true);
  }

  $(document).ready(function() {
    // Write when the cart already has items (settled state, no mutation in
    // flight). Skipped for empty carts so we don't create a cart — and inflate
    // abandoned-cart metrics — for visitors who never add anything.
    fetch('/cart.js', { cache: 'no-store' })
      .then(function(res) { return res.json(); })
      .then(function(cart) {
        if (cart && cart.item_count > 0) {
          _cartTouched = true;
          writeIdentifierAttributes(false);
        }
      })
      .catch(function() { /* ignore */ });

    // Adds only flag that there's a cart to write at exit — never write here.
    $(document).on('click',
      '[name="add"], .product-a-atc, .product-b-atc, .pdp-version-atc, ' +
      '.mic-android-atc, .ajax-cart__studio-add-btn, .subscription_btn',
      function() { _cartTouched = true; });

    // Primary stamp moment: the cart/drawer "Checkout" button (a native
    // <button name="checkout"> form submit). At click time the cart is settled,
    // so the write can't race a line-item mutation. We await the stamp, then let
    // the native submit proceed — this stamps the cart deterministically instead
    // of relying on the lossy pagehide keepalive, and covers every add flow since
    // they all funnel through this one button. The hard fallback guarantees we
    // never block checkout if the write is slow. (Express/Shop-Pay buttons are a
    // separate component — not name="checkout" — and fall back to pagehide.)
    $(document).on('click', 'button[name="checkout"]:not([disabled])', function(e) {
      var btn = this;
      if (btn._audigoStamping) { return; }  // re-dispatched click → native submit proceeds
      e.preventDefault();
      e.stopPropagation();
      btn._audigoStamping = true;
      var done = false;
      var proceed = function() {
        if (done) { return; }
        done = true;
        btn.click();  // native re-click submits the form with name=checkout intact
      };
      try {
        Promise.resolve(stampIdentifiersAwaitable()).then(proceed, proceed);
      } catch (err) { proceed(); }
      setTimeout(proceed, 1600);  // never block the redirect
    });

    window.addEventListener('pagehide', writeIdentifiersOnExit);
  });
})();

// ============================================================
// TOGGLE: Set to true to show popup upsell, false for direct checkout
// ============================================================
const SHOW_POPUP_UPSELL = true;


// Expose to global scope so VWO can access it
window.currentVariantId = null;

// ============================================================
// Android Device Detection
// ============================================================
window.isAndroidDevice = function() {
  // Check for test parameter first
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('test_android') === 'true') {
    return true;
  }
  // Check user agent for actual Android devices
  return /Android/i.test(navigator.userAgent);
};

// Store detection result globally
window.isAndroid = window.isAndroidDevice();

// ============================================================
// Android "Shop" routing (sitewide)
// On Android devices, links to the iOS PDP go to the Android PDP instead.
// Skips the deliberate cross-links ("Switch to iOS info", "See all mic options")
// and the cart drawer, whose line items must link to the product actually in the cart.
// iOS ?variant= params are dropped since those variants don't exist on the Android product.
// ============================================================
$(function() {
  if (!window.isAndroid) { return; }
  var IOS = 'wireless-mic-and-ios-app';
  var ANDROID = 'wireless-mic-and-android-app';
  function toAndroid(href) {
    return href.replace(IOS, ANDROID).replace(/([?&])variant=\d+&?/, '$1').replace(/[?&]$/, '');
  }
  $('a[href*="/products/' + IOS + '"]').not('.cross-nav-link__anchor').each(function() {
    if ($(this).closest('.ajax-cart').length) { return; }
    this.setAttribute('href', toAndroid(this.getAttribute('href')));
  });
  // Studio+ carousel modals build their "Shop now" link from hidden data at open time
  $('[data-overlay-button-url]').each(function() {
    if (this.textContent.indexOf(IOS) !== -1) { this.textContent = toAndroid(this.textContent); }
  });
});

// Helper functions exposed globally for VWO injection
window.clearCartBeforeAdd = function() {
  return fetch("/cart/clear.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  })
    .then(() => console.log("Cart cleared before adding new product"))
    .catch((err) => console.warn("Cart clear failed before add", err));
};

// Placeholder - will be properly defined once addToCartAndShowDrawer is available
window.checkoutWithCurrentVariant = function() {
  console.error("checkoutWithCurrentVariant called before initialization");
  return Promise.resolve();
};

// Default US customer checkout behavior
// Controlled by SHOW_POPUP_UPSELL flag above
window.handleUSCustomerCheckout = function(variantId) {
  const allowedIds = [51380566524117, 51380566556885, 51380566589653, 51380566622421, 52795181498581, 52795181531349, 52795181564117, 52795181596885];
  
  // If popup upsell is enabled and this is an iOS mic variant
  if (SHOW_POPUP_UPSELL && allowedIds.includes(variantId)) {
    console.log("US customer (Case B): Showing popup for variant", variantId);
    $('#studioPlusMonthlyPopup').css("display", "flex");
  } else {
    // Direct to checkout (Case A)
    console.log("US customer (Case A): Going straight to checkout:", variantId);
    window.checkoutWithCurrentVariant();
  }
};

$(document).ready(function () {
  // Old overlay nav handlers — only bind if old nav elements exist
  if (document.querySelector('.custom_header .overlay')) {
    $(".hummenu_container").on("click", function () {
      $(".overlay").addClass("active");
      $("html").css("overflow", "hidden");
    });
    $(".overlay-close").on("click", function () {
      $(".overlay").removeClass("active");
      $("html").css("overflow", "");
    });
  }

  const $checkoutButtons = $(".popUpBtn");
  const $directCheckoutButtons = $(".no-upsell-button");
  const $checkoutPopup = $("#checkoutPopup");
  const $proPlanPopup = $("#proPlanPopup");
  const $studioPlusMonthlyPopup = $("#studioPlusMonthlyPopup");
  const $studioPlusAnnualPopup = $("#studioPlusAnnualPopup");
  const $justMicButton = $(".just-mic-button");
  const $closePopupBtn = $("#closeBTn");
  const $closeProPlanBtn = $("#closeProPlanBtn");
  const $closeStudioPlusMonthlyBtn = $("#closeStudioPlusMonthlyBtn");
  const $closeStudioPlusAnnualBtn = $("#closeStudioPlusAnnualBtn");
  const $proPlanLabel = $(".Pro_plan_label");
  const $studioPlanLabel = $(".studio_plan_label");

  $checkoutButtons.on("click", function () {
    window.currentVariantId = $(this).data("variant-id");
    console.log("Variant set:", window.currentVariantId);
    if (!window.currentVariantId) {
      alert("Error: Missing product variant. Please refresh the page.");
      return;
    }
    $checkoutPopup.css("display", "flex");
  });

  $directCheckoutButtons.on("click", function () {
    const $btn = $(this);
    window.currentVariantId = $btn.data("variant-id");
    window.currentButton = $btn; // Store button for VWO access
    const isUSCustomer = $btn.data("is-us-customer");
    
    console.log("Direct checkout variant set:", window.currentVariantId, "US customer:", isUSCustomer);
    
    if (!window.currentVariantId) {
      alert("Error: Missing product variant. Please refresh the page.");
      return;
    }
    
    // Non-US customers always go straight to checkout (no upsells)
    if (!isUSCustomer) {
      console.log("Non-US customer: bypassing upsells, going to checkout");
      checkoutWithCurrentVariant();
      return;
    }
    
    // US customers: use routing function (VWO can override)
    window.handleUSCustomerCheckout(window.currentVariantId);
  });

  $proPlanLabel.on("click", function () {
    $checkoutPopup.hide();
    $proPlanPopup.css("display", "flex");
  });
  $studioPlanLabel.on("click", function () {
    $proPlanPopup.hide();
    $checkoutPopup.css("display", "flex");
  });

  $justMicButton.on("click", function() {
    var micVariantId = window.currentVariantId;
    if (!micVariantId) { return; }

    if (window._pdpDirectToCheckout) {
      // Version C flow: clear cart → add mic only → redirect to /checkout
      window.clearCartBeforeAdd()
        .then(function() {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: micVariantId, quantity: 1 }] })
          });
        })
        .then(function() {
          // Stamp identifiers AFTER the add, BEFORE the redirect, and await it.
          // This flow cleared the cart (wiping any attributes a load-time write
          // had placed), so without this the only stamp left is the racy
          // pagehide keepalive — the cause of the halved coverage on
          // direct-to-checkout orders. Awaiting guarantees it commits first.
          return window.audigoStampIdentifiers ? window.audigoStampIdentifiers() : null;
        })
        .then(function() {
          window.location.href = '/checkout';
        })
        .catch(function(err) {
          console.error('[JustMic] checkout error:', err);
          $('.checkout-popup').hide();
          window._pdpDirectToCheckout = false;
        });
      return;
    }

    // A/B flow: add to cart and show drawer
    fetch('/cart.js', { cache: 'no-store' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        if (tryIosMicConsolidation(null, micVariantId, null, cart)) {
          setTimeout(function() { $('.checkout-popup').hide(); }, 500);
          return;
        }
        $('.checkout-popup').hide();
        addToCartAndShowDrawer(micVariantId);
      });
  });

  $(document).on('click', '.mic-android-atc', function() {
    var $btn = $(this);
    var variantId = $btn.data('variant-id');
    if (!variantId) {
      alert("Error: Missing product variant. Please refresh the page.");
      return;
    }
    $btn.prop('disabled', true).text('Adding…');

    // Cap: only 1 Android mic allowed in cart
    fetch('/cart.js', { cache: 'no-store' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var hasAndroid = cart.items.some(function(item) {
          return item.handle === 'wireless-mic-and-android-app';
        });
        if (hasAndroid) {
          // Already in cart — show the drawer with max-qty message
          $btn.prop('disabled', false).text('Add to cart');
          window._audigoAndroidMaxMsg = true;
          Shopify.theme.jsAjaxCart.updateView();
          Shopify.theme.jsAjaxCart.showDrawer();
        } else {
          // US: attach the Studio+ trial (same as iOS) unless the cart already has one
          var subVariant  = $btn.data('subscription-variant-id');
          var sellingPlan = $btn.data('selling-plan-id');
          var hasExistingSub = cart.items.some(function(i) { return !!i.selling_plan_allocation; });
          if (subVariant && sellingPlan && !hasExistingSub) {
            var micItem = { id: variantId, quantity: 1 };
            var subItem = {
              id: parseInt(subVariant),
              quantity: 1,
              selling_plan: parseInt(sellingPlan),
              properties: { _sub_has_trial: 'true', _sub_trial_len: '30', _sub_trial_period: 'days' }
            };
            doProductAAtcAdd($btn, subItem, micItem, micItem);
          } else {
            addToCartAndShowDrawer(variantId, $btn);
          }
        }
      })
      .catch(function() {
        // On error, just try to add normally
        addToCartAndShowDrawer(variantId, $btn);
      });
  });

  function addToCartAndShowDrawer(variantId, $btn) {
    console.log("Adding to cart:", variantId);
    $.ajax({
      url: "/cart/add.js",
      method: "POST",
      dataType: "json",
      data: {
        items: [
          {
            id: variantId,
            quantity: 1,
          },
        ],
      },
      success: function (response) {
        console.log("Cart response:", response);
        Shopify.theme.jsAjaxCart.updateView();
        Shopify.theme.jsAjaxCart.showDrawer();
        if ($btn) $btn.prop('disabled', false).text('Add to cart');
      },
      error: function (error) {
        console.error("Cart error:", error.responseJSON || error);
        alert(
          `Error: ${
            error.responseJSON?.description || "Could not add to cart."
          }`
        );
        if ($btn) $btn.prop('disabled', false).text('Add to cart');
      },
    });
  }

  // Now that addToCartAndShowDrawer is defined, properly set up the global function
  window.checkoutWithCurrentVariant = function() {
    if (!window.currentVariantId) {
      alert("Please select a plan first.");
      return Promise.resolve();
    }

    addToCartAndShowDrawer(window.currentVariantId);
  };

  var _addOnPlusInProgress = false;

  // Clean up popup state when user navigates back via bfcache
  window.addEventListener('pageshow', function(e) {
    if (e.persisted) {
      _addOnPlusInProgress = false;
      window._pdpDirectToCheckout = false;
      $('.checkout-popup, #studioPlusMonthlyPopup, #studioPlusAnnualPopup, #checkoutPopup, #proPlanPopup').hide();
      $('.subscription_btn').each(function() {
        var $btn = $(this);
        if ($btn.data('original-html')) {
          $btn.html($btn.data('original-html')).removeClass('focused');
        }
      });
    }
  });
  const AddOnPlusMainVariantAddToCart = (mainBtn) => {
    if (_addOnPlusInProgress) { console.log('[Popup] blocked duplicate call'); return; }
    _addOnPlusInProgress = true;
    let finalProdForCart = [];
    // Prefer subscription data stored by the PDP button (same source as A/B),
    // fall back to the popup button's metafield-based attrs for backward compat.
    const addOnVariantId = window._pdpSubVariantId || mainBtn.attr("data-sub-variant-id");
    const sellingPlanId = window._pdpSellingPlanId || mainBtn.attr("data-selling-plan-id");
    const planName = mainBtn.attr("data-planName");
    // Clear stored values so they don't leak to future popup invocations
    window._pdpSubVariantId = null;
    window._pdpSellingPlanId = null;

    // Determine subscription properties based on plan type
    let subscriptionProperties = {};
    if (planName === "Studio Plus Monthly") {
      subscriptionProperties = {
        _sub_len: "1",
        _sub_period: "month",
        _sub_has_trial: "true",
        _sub_trial_len: "30",
        _sub_trial_period: "days",
        _sub_price: "15",
        _sub_spid: String(sellingPlanId),
      };
    } else if (planName === "Studio Plus Annual") {
      subscriptionProperties = {
        _sub_len: "1",
        _sub_period: "year",
        _sub_has_trial: "true",
        _sub_trial_len: "30",
        _sub_trial_period: "days",
        _sub_price: "99",
        _sub_spid: String(sellingPlanId),
      };
    }

    if (!mainBtn.data("original-html")) {
      mainBtn.data("original-html", mainBtn.html());
    }

    // Keep popup visible with loading state until cart drawer is ready
    mainBtn
      .empty()
      .html(`<span class="button is-loading is-loading--icon-only"></span>`)
      .addClass("focused");

    var micVariantId = window.currentVariantId;
    var directToCheckout = !!window._pdpDirectToCheckout;

    function _dismissPopup() {
      $('.checkout-popup').hide();
      $('#studioPlusMonthlyPopup').hide();
      mainBtn.empty().html(mainBtn.data("original-html")).removeClass("focused");
      _addOnPlusInProgress = false;
      window._pdpDirectToCheckout = false;
    }

    if (directToCheckout) {
      // Version C flow: clear cart → add items → redirect to /checkout
      var subItem = {
        id: addOnVariantId,
        quantity: 1,
        selling_plan: sellingPlanId,
        properties: subscriptionProperties,
      };
      var itemsToAdd = [subItem, { id: micVariantId, quantity: 1, properties: {} }];

      window.clearCartBeforeAdd()
        .then(function() {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: itemsToAdd })
          });
        })
        .then(function() {
          // Stamp identifiers AFTER the add, BEFORE the redirect, and await it.
          // This is the mixed-cart (mic + trial) path: it cleared the cart,
          // wiping any earlier attribute write, so the awaited stamp here is what
          // pulls mixed-cart coverage up to the hardware-only level.
          return window.audigoStampIdentifiers ? window.audigoStampIdentifiers() : null;
        })
        .then(function() {
          window.location.href = '/checkout';
        })
        .catch(function(err) {
          console.error('[AddOnPlus] checkout error:', err);
          _dismissPopup();
        });
      return;
    }

    // A/B flow: add to cart and show drawer
    fetch('/cart.js', { cache: 'no-store' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var hasExistingSub = cart.items.some(function(i) { return !!i.selling_plan_allocation; });
        var subItem = hasExistingSub ? null : {
          id: addOnVariantId,
          quantity: 1,
          selling_plan: sellingPlanId,
          properties: subscriptionProperties,
        };

        function _dismissPopupAndShowDrawer() {
          _dismissPopup();
        }

        // Poll for cart drawer DOM update, then dismiss popup + show drawer together
        function _waitForCartThenDismiss() {
          var _cartBefore = $('.ajax-cart__list').html();
          var _poll = setInterval(function() {
            if ($('.ajax-cart__list').html() !== _cartBefore) {
              clearInterval(_poll);
              _dismissPopupAndShowDrawer();
              Shopify.theme.jsAjaxCart.showDrawer();
            }
          }, 50);
          setTimeout(function() {
            clearInterval(_poll);
            _dismissPopupAndShowDrawer();
            Shopify.theme.jsAjaxCart.showDrawer();
          }, 4000);
        }

        // Try iOS mic consolidation (handles existing mic swapping + optional sub add)
        if (tryIosMicConsolidation(null, micVariantId, subItem, cart)) {
          console.log('[Popup] consolidation path');
          _waitForCartThenDismiss();
          return;
        }

        console.log('[Popup] normal add path');
        // No existing mic — normal add
        var itemsToAdd = [];
        if (subItem) { itemsToAdd.push(subItem); }
        itemsToAdd.push({ id: micVariantId, quantity: 1, properties: {} });

        $.ajax({
          type: "POST",
          url: "/cart/add.js",
          data: { items: itemsToAdd },
          dataType: "json",
          success: function () {
            console.log('[Popup] AJAX success');
            Shopify.theme.jsAjaxCart.updateView();
            _waitForCartThenDismiss();
          },
          error: function (xhr) {
            console.error('[AddOnPlus] Cart add error:', xhr.responseJSON || xhr.statusText);
            _dismissPopupAndShowDrawer();
          },
        });
      });
  };

  $closePopupBtn.on("click", () => $checkoutPopup.hide());
  $closeProPlanBtn.on("click", () => $proPlanPopup.hide());
  $closeStudioPlusMonthlyBtn.on("click", () => { $studioPlusMonthlyPopup.hide(); window._pdpDirectToCheckout = false; });
  $closeStudioPlusAnnualBtn.on("click", () => $studioPlusAnnualPopup.hide());
  $(document).on("click", ".subscription_btn", function () {
    AddOnPlusMainVariantAddToCart($(this));
  });

  $closePopupBtn.on("click.preventReopen", function (e) {
    e.preventDefault();
    e.stopPropagation();
    setTimeout(() => {
      $checkoutPopup.css("display", "flex");
      $("html").css("overflow", "hidden");
    }, 0);
  });
  $closeProPlanBtn.on("click.preventReopen", function (e) {
    e.preventDefault();
    e.stopPropagation();
    setTimeout(() => {
      $proPlanPopup.css("display", "flex");
      $("html").css("overflow", "hidden");
    }, 0);
  });
  $closeStudioPlusMonthlyBtn.on("click.preventReopen", function (e) {
    e.preventDefault();
    e.stopPropagation();
    setTimeout(() => {
      $studioPlusMonthlyPopup.css("display", "flex");
      $("html").css("overflow", "hidden");
    }, 0);
  });
  $closeStudioPlusAnnualBtn.on("click.preventReopen", function (e) {
    e.preventDefault();
    e.stopPropagation();
    setTimeout(() => {
      $studioPlusAnnualPopup.css("display", "flex");
      $("html").css("overflow", "hidden");
    }, 0);
  });

  $checkoutPopup.on("mousedown.dismissOutside", function (e) {
    if (e.target === this) {
      $(this).hide();
      $("html").css("overflow", "");
      popModalStateIfAny(); 
    }
  });
  $proPlanPopup.on("mousedown.dismissOutside", function (e) {
    if (e.target === this) {
      $(this).hide();
      $("html").css("overflow", "");
      popModalStateIfAny();
    }
  });
  $studioPlusMonthlyPopup.on("mousedown.dismissOutside", function (e) {
    if (e.target === this) {
      $(this).hide();
      $("html").css("overflow", "");
      popModalStateIfAny();
    }
  });
  $studioPlusAnnualPopup.on("mousedown.dismissOutside", function (e) {
    if (e.target === this) {
      $(this).hide();
      $("html").css("overflow", "");
      popModalStateIfAny();
    }
  });

  $(document).on("mousedown.dismissOutsideDoc", function (e) {
    if ($checkoutPopup.is(":visible") && !$(e.target).closest("#checkoutPopup").length) {
      $checkoutPopup.hide();
      $("html").css("overflow", "");
      popModalStateIfAny();
    }
    if ($proPlanPopup.is(":visible") && !$(e.target).closest("#proPlanPopup").length) {
      $proPlanPopup.hide();
      $("html").css("overflow", "");
      popModalStateIfAny();
    }
    if ($studioPlusMonthlyPopup.is(":visible") && !$(e.target).closest("#studioPlusMonthlyPopup").length) {
      $studioPlusMonthlyPopup.hide();
      $("html").css("overflow", "");
      popModalStateIfAny();
    }
    if ($studioPlusAnnualPopup.is(":visible") && !$(e.target).closest("#studioPlusAnnualPopup").length) {
      $studioPlusAnnualPopup.hide();
      $("html").css("overflow", "");
      popModalStateIfAny();
    }
  });

  $(document).on("keydown.dismissEsc", function (e) {
    if (e.key === "Escape" || e.keyCode === 27) {
      let closed = false;
      if ($checkoutPopup.is(":visible")) {
        $checkoutPopup.hide();
        closed = true;
      }
      if ($proPlanPopup.is(":visible")) {
        $proPlanPopup.hide();
        closed = true;
      }
      if ($studioPlusMonthlyPopup.is(":visible")) {
        $studioPlusMonthlyPopup.hide();
        closed = true;
      }
      if ($studioPlusAnnualPopup.is(":visible")) {
        $studioPlusAnnualPopup.hide();
        closed = true;
      }
      if (closed) {
        $("html").css("overflow", "");
        popModalStateIfAny();
      }
    }
  });

  let _activeModalKey = null; 

  function pushModalState(key) {
    if (_activeModalKey) return;
    try {
      history.pushState({ popup: key }, "", location.href);
      _activeModalKey = key;
    } catch (e) {}
  }

  function popModalStateIfAny() {
    if (!_activeModalKey) return;
    const key = _activeModalKey;
    _activeModalKey = null;
    try {
      history.back();
    } catch (e) {}
  }

  $checkoutButtons.on("click.historyHook", function () {
    setTimeout(() => {
      if ($checkoutPopup.is(":visible")) pushModalState("checkout");
    }, 0);
  });
  $proPlanLabel.on("click.historyHook", function () {
    setTimeout(() => {
      if ($proPlanPopup.is(":visible")) pushModalState("pro");
    }, 0);
  });
  $studioPlanLabel.on("click.historyHook", function () {
    setTimeout(() => {
      if ($checkoutPopup.is(":visible")) pushModalState("checkout");
    }, 0);
  });

  const mo = new MutationObserver(() => {
    const checkoutOpen = $checkoutPopup.is(":visible");
    const proOpen = $proPlanPopup.is(":visible");
    const studioPlusMonthlyOpen = $studioPlusMonthlyPopup.is(":visible");
    const studioPlusAnnualOpen = $studioPlusAnnualPopup.is(":visible");
    if (checkoutOpen && !_activeModalKey) pushModalState("checkout");
    else if (proOpen && !_activeModalKey) pushModalState("pro");
    else if (studioPlusMonthlyOpen && !_activeModalKey) pushModalState("studioPlusMonthly");
    else if (studioPlusAnnualOpen && !_activeModalKey) pushModalState("studioPlusAnnual");
  });
  if ($checkoutPopup[0]) mo.observe($checkoutPopup[0], { attributes: true, attributeFilter: ["style", "class"] });
  if ($proPlanPopup[0]) mo.observe($proPlanPopup[0], { attributes: true, attributeFilter: ["style", "class"] });
  if ($studioPlusMonthlyPopup[0]) mo.observe($studioPlusMonthlyPopup[0], { attributes: true, attributeFilter: ["style", "class"] });
  if ($studioPlusAnnualPopup[0]) mo.observe($studioPlusAnnualPopup[0], { attributes: true, attributeFilter: ["style", "class"] });

  window.addEventListener("popstate", function () {
    if ($checkoutPopup.is(":visible") || $proPlanPopup.is(":visible") || $studioPlusMonthlyPopup.is(":visible") || $studioPlusAnnualPopup.is(":visible")) {
      $checkoutPopup.hide();
      $proPlanPopup.hide();
      $studioPlusMonthlyPopup.hide();
      $studioPlusAnnualPopup.hide();
      $("html").css("overflow", "");
      _activeModalKey = null;
      return;
    }
    if (_activeModalKey) _activeModalKey = null;
  });

  // ============================================================
  // Cart Drawer Quantity Loading State
  // Disables the qty pill immediately on tap so users get instant
  // feedback. updateView() replaces the DOM, clearing state automatically.
  // ============================================================
  $(document).on('click', '.ajax-cart [data-update-quantity]', function () {
    var $pill = $(this).closest('.ajax-cart__qty-pill');
    $pill.addClass('is-updating');
    $pill.find('[data-update-quantity]').attr('disabled', true);
  });
});

// ============================================================
// Product A-variant — Qty Dropdown
// ============================================================
$(document).on('click', '.product-a-qty-trigger, .product-a-qty-header', function () {
  var $menu = $(this).closest('.product-a-qty-wrapper').find('.product-a-qty-menu');
  $menu.prop('hidden', !$menu.prop('hidden'));
});

$(document).on('click', '.product-a-qty-row', function () {
  var $row       = $(this);
  var label      = $row.data('label');
  var price      = $row.data('price');
  var variantId  = $row.data('variant-id');
  var compare    = $row.data('compare') || '';
  var save       = $row.data('save') || '';
  var $right     = $row.closest('.product-a-right');

  $right.find('.product-a-qty-label').text(label);
  $right.find('.product-a-qty-row').removeAttr('data-selected');
  $row.attr('data-selected', 'true');
  $right.find('.product-a-price').text(price);
  $right.find('.product-a-compare-price').text(compare).prop('hidden', !compare);
  $right.find('.product-a-trigger-save').text(save).prop('hidden', !save);
  $right.find('.product-a-save-multiple').prop('hidden', false);
  $right.find('.product-a-atc').data('variant-id', variantId);
  $right.find('.product-a-qty-menu').prop('hidden', true);
});

// PDP rating summary: smooth-scroll to the Judge.me reviews carousel lower on the page.
// The carousel is an app block, so target its section (template section key ends in
// __1787029103d22fc6ae), then fall back to the carousel markup or the full widget.
$(document).on('click', '[data-pdp-reviews-link]', function (e) {
  var target = document.querySelector('[id$="__1787029103d22fc6ae"]') ||
    document.querySelector('.jdgm-carousel-wrapper, .jdgm-carousel') ||
    document.getElementById('judgeme_product_reviews');
  if (!target) return; // let the plain #anchor behave normally
  e.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

$(document).on('click', function (e) {
  if (!$(e.target).closest('.product-a-qty-wrapper').length) {
    $('.product-a-qty-menu').prop('hidden', true);
  }
});

// ============================================================
// iOS mic consolidation — shared by both A and B ATC handlers.
// Returns true if consolidation handled the add; false if the
// caller should proceed with the normal (non-consolidation) add.
// ============================================================
function tryIosMicConsolidation($btn, variantId, subItem, cart) {
  var _map = window.AUDIGO_IOS_MIC_MAP;
  if (!_map) { return false; }

  var _addingCount = null;
  for (var _k in _map) {
    if (parseInt(_map[_k]) === variantId) { _addingCount = parseInt(_k); break; }
  }
  if (_addingCount === null) { return false; }

  // Build reverse lookup: variant_id (string) → mic count
  var _valToCount = {};
  for (var _j in _map) { _valToCount[String(_map[_j])] = parseInt(_j); }
  var _existingMicItems = cart.items.filter(function(item) {
    return String(item.variant_id) in _valToCount;
  });
  if (_existingMicItems.length === 0) { return false; }

  // Safeguard: if any iOS mic line item has qty > 1, normalize to 1 first
  var _qtyFixes = {};
  _existingMicItems.forEach(function(item) {
    if (item.quantity > 1) { _qtyFixes[item.key] = 1; }
  });
  if (Object.keys(_qtyFixes).length > 0) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: _qtyFixes })
    })
    .then(function() {
      if ($btn) { $btn.prop('disabled', false).text('Add to cart'); $btn.trigger('click'); }
    })
    .catch(function() {
      Shopify.theme.jsAjaxCart.updateView();
      Shopify.theme.jsAjaxCart.showDrawer();
      if ($btn) { $btn.prop('disabled', false).text('Add to cart'); }
    });
    return true;
  }

  var _existingTotal = _existingMicItems.reduce(function(sum, item) {
    return sum + (_valToCount[String(item.variant_id)] * item.quantity);
  }, 0);
  var _maxMics = window.AUDIGO_IOS_MIC_MAX || 4;
  var _rawTotal = _existingTotal + _addingCount;
  var _newTotal = Math.min(_rawTotal, _maxMics);
  if (_rawTotal > _maxMics) { window._audigoRevealMaxQtyMsg = true; }
  var _newVariantId = parseInt(_map[String(_newTotal)]);

  var _removals = {};
  _existingMicItems.forEach(function(item) {
    if (item.variant_id !== _newVariantId) { _removals[item.key] = 0; }
  });
  var _needsAdd = !_existingMicItems.some(function(item) {
    return item.variant_id === _newVariantId;
  });

  // Fast-path: mic already at target state — nothing to swap
  if (!_needsAdd && Object.keys(_removals).length === 0) {
    if (subItem) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [subItem] })
      })
      .then(function() {
        Shopify.theme.jsAjaxCart.updateView();
        Shopify.theme.jsAjaxCart.showDrawer();
        scheduleAudigoCartValidation();
        if ($btn) { $btn.prop('disabled', false).text('Add to cart'); }
      })
      .catch(function() {
        Shopify.theme.jsAjaxCart.updateView();
        Shopify.theme.jsAjaxCart.showDrawer();
        if ($btn) { $btn.prop('disabled', false).text('Add to cart'); }
      });
    } else {
      Shopify.theme.jsAjaxCart.updateView();
      Shopify.theme.jsAjaxCart.showDrawer();
      if ($btn) { $btn.prop('disabled', false).text('Add to cart'); }
    }
    return true;
  }

  // Swap: add new variant, remove old, optionally add subscription
  var _chain = Promise.resolve();

  if (_needsAdd) {
    _chain = _chain.then(function() {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: _newVariantId, quantity: 1 }] })
      }).then(function(r) { return r.json(); });
    });
  }

  if (Object.keys(_removals).length > 0) {
    _chain = _chain.then(function() {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: _removals })
      });
    });
  }

  if (subItem) {
    _chain = _chain.then(function() {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [subItem] })
      });
    });
  }

  _chain
    .then(function() {
      Shopify.theme.jsAjaxCart.updateView();
      Shopify.theme.jsAjaxCart.showDrawer();
      scheduleAudigoCartValidation();
      if ($btn) { $btn.prop('disabled', false).text('Add to cart'); }
    })
    .catch(function(err) {
      console.error('[iOS mic consolidation] error:', err);
      Shopify.theme.jsAjaxCart.updateView();
      Shopify.theme.jsAjaxCart.showDrawer();
      if ($btn) { $btn.prop('disabled', false).text('Add to cart'); }
    });
  return true;
}

// ============================================================
// Product A-variant — Add to Cart
// ============================================================
$(document).on('click', '.product-a-atc', function () {
  var $btn        = $(this);
  var variantId   = $btn.data('variant-id');
  var qty         = parseInt($btn.data('qty') || 1);
  var subVariant  = $btn.data('subscription-variant-id');
  var sellingPlan = $btn.data('selling-plan-id');

  var micItem = { id: variantId, quantity: qty };
  var subItem = (subVariant && sellingPlan) ? {
    id: parseInt(subVariant),
    quantity: 1,
    selling_plan: parseInt(sellingPlan),
    properties: { _sub_has_trial: 'true', _sub_trial_len: '30', _sub_trial_period: 'days' }
  } : null;

  $btn.prop('disabled', true).text('Adding…');

  fetch('/cart.js', { cache: 'no-store' })
    .then(function(r) { return r.json(); })
    .then(function(cart) {
      var hasExistingSub = cart.items.some(function(i) { return !!i.selling_plan_allocation; });
      if (hasExistingSub) { subItem = null; }

      // Try iOS mic consolidation first
      if (tryIosMicConsolidation($btn, variantId, subItem, cart)) { return; }

      // No consolidation needed — normal add
      var firstItem  = subItem || micItem;
      var secondItem = subItem ? micItem : null;
      doProductAAtcAdd($btn, firstItem, secondItem, micItem);
    });
});

function doProductAAtcAdd($btn, firstItem, secondItem, micItem) {
  $.ajax({
    url: '/cart/add.js',
    method: 'POST',
    dataType: 'json',
    data: { items: [firstItem] },
    success: function () {
      if (!secondItem) {
        Shopify.theme.jsAjaxCart.updateView();
        Shopify.theme.jsAjaxCart.showDrawer();
        scheduleAudigoCartValidation();
        return;
      }
      $.ajax({
        url: '/cart/add.js',
        method: 'POST',
        dataType: 'json',
        data: { items: [secondItem] },
        success: function () {
          Shopify.theme.jsAjaxCart.updateView();
          Shopify.theme.jsAjaxCart.showDrawer();
          scheduleAudigoCartValidation();
        },
        error: function (err) {
          var resp = err.responseJSON || {};
          console.error('[product-a-atc] mic add failed:', JSON.stringify(resp, null, 2));
          // Subscription already added — open cart anyway
          Shopify.theme.jsAjaxCart.updateView();
          Shopify.theme.jsAjaxCart.showDrawer();
          scheduleAudigoCartValidation();
        }
      });
    },
    error: function (err) {
      var resp = err.responseJSON || {};
      console.error('[product-a-atc] subscription add failed:', JSON.stringify(resp, null, 2));
      // Subscription failed — try adding mic on its own
      $.ajax({
        url: '/cart/add.js',
        method: 'POST',
        dataType: 'json',
        data: { items: [micItem] },
        success: function () {
          Shopify.theme.jsAjaxCart.updateView();
          Shopify.theme.jsAjaxCart.showDrawer();
        },
        error: function (err2) {
          var resp2 = err2.responseJSON || {};
          console.error('[product-a-atc] mic add also failed:', JSON.stringify(resp2, null, 2));
          alert(resp2.description || 'Could not add to cart.');
        },
        complete: function () {
          $btn.prop('disabled', false).text('Add to cart');
        }
      });
    },
    complete: function () {
      $btn.prop('disabled', false).text('Add to cart');
    }
  });
}

// ============================================================
// Product B-variant — Mic Quantity Pills
// ============================================================
$(document).on('click', '.product-b-mic-pill', function () {
  var $pill      = $(this);
  var variantId  = $pill.data('variant-id');
  var price      = $pill.data('price');
  var compare    = $pill.data('compare') || '';
  var save       = $pill.data('save') || '';
  var $right     = $pill.closest('.product-b-right');

  $right.find('.product-b-mic-pill').removeClass('is-selected');
  $pill.addClass('is-selected');
  $right.find('.product-b-price').text(price);
  $right.find('.product-b-compare-price').text(compare).prop('hidden', !compare);
  $right.find('.product-b-savings').text(save).prop('hidden', !save);
  $right.find('.product-b-save-multiple').prop('hidden', !$pill.is(':first-child'));
  $right.find('.product-b-atc').data('variant-id', variantId);
});

// ============================================================
// Product B-variant — Studio+ Checkbox Toggle
// ============================================================
$(document).on('click', '.product-b-studio-toggle', function () {
  var $toggle = $(this);
  var isChecked = $toggle.hasClass('is-checked');
  var $right = $toggle.closest('.product-b-right');

  $toggle.toggleClass('is-checked');
  var newAccess = isChecked ? 'studio' : 'studio-plus';
  $toggle.attr('data-access', newAccess);
  $right.find('.product-b-atc').data('access', newAccess);
});

// ============================================================
// Product B-variant — Add to Cart
// ============================================================
$(document).on('click', '.product-b-atc', function () {
  var $btn        = $(this);
  var variantId   = $btn.data('variant-id');
  var qty         = parseInt($btn.data('qty') || 1);
  var access      = $btn.data('access') || 'studio-plus';
  var subVariant  = $btn.data('subscription-variant-id');
  var sellingPlan = $btn.data('selling-plan-id');

  var micItem = { id: variantId, quantity: qty };
  var addSub  = (access === 'studio-plus') && subVariant && sellingPlan;
  var subItem = addSub ? {
    id: parseInt(subVariant),
    quantity: 1,
    selling_plan: parseInt(sellingPlan),
    properties: { _sub_has_trial: 'true', _sub_trial_len: '30', _sub_trial_period: 'days' }
  } : null;

  $btn.prop('disabled', true).text('Adding…');

  // Check if subscription already in cart; if so, skip adding another
  fetch('/cart.js', { cache: 'no-store' })
    .then(function(r) { return r.json(); })
    .then(function(cart) {
      var hasExistingSub = cart.items.some(function(i) { return !!i.selling_plan_allocation; });
      if (hasExistingSub) { subItem = null; }

      // Try iOS mic consolidation first (same logic as A handler)
      if (tryIosMicConsolidation($btn, variantId, subItem, cart)) { return; }

      // No consolidation needed — normal add
      var firstItem  = micItem;
      var secondItem = subItem || null;

      $.ajax({
        url: '/cart/add.js',
        method: 'POST',
        dataType: 'json',
        data: { items: [firstItem] },
        success: function () {
          if (!secondItem) {
            Shopify.theme.jsAjaxCart.updateView();
            Shopify.theme.jsAjaxCart.showDrawer();
            scheduleAudigoCartValidation();
            return;
          }
          $.ajax({
            url: '/cart/add.js',
            method: 'POST',
            dataType: 'json',
            data: { items: [secondItem] },
            success: function () {
              Shopify.theme.jsAjaxCart.updateView();
              Shopify.theme.jsAjaxCart.showDrawer();
              scheduleAudigoCartValidation();
            },
            error: function (err) {
              var resp = err.responseJSON || {};
              console.error('[product-b-atc] sub add failed:', JSON.stringify(resp, null, 2));
              Shopify.theme.jsAjaxCart.updateView();
              Shopify.theme.jsAjaxCart.showDrawer();
              scheduleAudigoCartValidation();
            }
          });
        },
        error: function (err) {
          var resp = err.responseJSON || {};
          console.error('[product-b-atc] mic add failed:', JSON.stringify(resp, null, 2));
          $btn.prop('disabled', false).text('Add to cart');
        },
        complete: function () {
          $btn.prop('disabled', false).text('Add to cart');
        }
      });
    });
});

// ============================================================
// Generic PDP Version — Add to Cart
// Used by future versions (C, D, …). Existing A/B handlers take
// priority via the early-return guard below.
//
// US customers: shows subscription upsell popup (same as production).
// Non-US customers: adds mic directly to cart (no subscription).
// ============================================================
$(document).on('click', '.pdp-version-atc', function () {
  var $btn = $(this);
  // Let existing A/B handlers fire instead if present
  if ($btn.hasClass('product-a-atc') || $btn.hasClass('product-b-atc')) { return; }

  var variantId   = $btn.data('variant-id');
  var subVariant  = $btn.data('subscription-variant-id');
  var sellingPlan = $btn.data('selling-plan-id');
  var isUS        = !!(subVariant && sellingPlan);

  // Version C uses direct-to-checkout flow (clear cart → add → redirect)
  window._pdpDirectToCheckout = true;

  if (isUS && SHOW_POPUP_UPSELL && !window.DISABLE_SUBSCRIPTION_POPUP) {
    // US flow: store variant + subscription data from the PDP button,
    // then show the popup. The popup handler uses these stored values
    // (not the popup button's metafield-based attrs) to ensure the
    // correct selling plan for this store.
    window.currentVariantId = variantId;
    window._pdpSubVariantId = subVariant;
    window._pdpSellingPlanId = sellingPlan;
    $('#studioPlusMonthlyPopup').css('display', 'flex');
  } else {
    // No-popup flow: clear cart, add items, redirect to checkout
    $btn.prop('disabled', true).text('Adding…');
    var micItem = { id: variantId, quantity: parseInt($btn.data('qty') || 1) };
    var subItem = isUS ? {
      id: parseInt(subVariant),
      quantity: 1,
      selling_plan: parseInt(sellingPlan),
      properties: { _sub_has_trial: 'true', _sub_trial_len: '30', _sub_trial_period: 'days' }
    } : null;
    var itemsToAdd = [];
    if (subItem) { itemsToAdd.push(subItem); }
    itemsToAdd.push(micItem);
    window.clearCartBeforeAdd()
      .then(function() {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: itemsToAdd })
        });
      })
      .then(function() {
        // Stamp identifiers AFTER the add, BEFORE the redirect, and await it.
        // This flow cleared the cart (wiping any earlier attribute write), so
        // the awaited stamp here replaces reliance on the racy pagehide keepalive.
        return window.audigoStampIdentifiers ? window.audigoStampIdentifiers() : null;
      })
      .then(function() {
        window.location.href = '/checkout';
      })
      .catch(function(err) {
        console.error('[Version C] checkout error:', err);
        $btn.prop('disabled', false).text('Checkout');
        window._pdpDirectToCheckout = false;
      });
  }
});

// ============================================================
// Landing page: "Order Now" buttons scroll to PDP embed section
// ============================================================
$(document).on('click', '.drums_over_section .btom_button, .drums-order-now-mid .btom_button', function(e) {
  var $target = $('#pdp-embed-anchor');
  if ($target.length) {
    e.preventDefault();
    $('html, body').animate({ scrollTop: $target.offset().top - 80 }, 600);
  }
});

// ============================================================
// Landing page: PDP product image carousel
// ============================================================
$(function() {
  var $carousel = $('.lp-pdp-carousel');
  if (!$carousel.length) return;
  var $slides = $carousel.find('.lp-pdp-carousel__slide');
  var $dots = $carousel.find('.lp-pdp-dot');
  if ($slides.length < 2) return;
  var current = 0;

  // Preload all images
  $slides.find('img').each(function() {
    var src = $(this).attr('src') || $(this).data('src');
    if (src) { new Image().src = src; }
  });

  function show(idx) {
    $slides.removeClass('active').eq(idx).addClass('active');
    $dots.removeClass('active').eq(idx).addClass('active');
    current = idx;
  }

  $dots.on('click', function() { show($(this).data('index')); });

  // Swipe
  var startX = 0;
  $carousel[0].addEventListener('touchstart', function(e) {
    startX = e.changedTouches[0].screenX;
  }, { passive: true });
  $carousel[0].addEventListener('touchend', function(e) {
    var diff = startX - e.changedTouches[0].screenX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && current < $slides.length - 1) { show(current + 1); }
      else if (diff < 0 && current > 0) { show(current - 1); }
    }
  }, { passive: true });
});

// ============================================================
// Landing page: PDP drawer toggle
// ============================================================
$(document).on('click', '.pdp-drawer__header', function() {
  var $drawer = $(this).closest('.pdp-drawer');
  var $content = $drawer.find('.pdp-drawer__content');
  var isOpen = $drawer.hasClass('is-open');
  $drawer.siblings('.pdp-drawer.is-open').removeClass('is-open')
    .find('.pdp-drawer__content').slideUp(200);
  $drawer.siblings('.pdp-drawer').find('.pdp-drawer__header').attr('aria-expanded', 'false');
  if (!isOpen) {
    $drawer.addClass('is-open');
    $content.slideDown(200);
    $(this).attr('aria-expanded', 'true');
  } else {
    $drawer.removeClass('is-open');
    $content.slideUp(200);
    $(this).attr('aria-expanded', 'false');
  }
});

// ============================================================
// Landing page: mobile phone gallery dot navigation
// ============================================================
$(function() {
  var $section = $('.multimic_phone_screens_section');
  if (!$section.length) return;
  var $wrapper = $section.find('.gallery__wrapper');

  function isMobile() { return window.innerWidth <= 798; }

  function initDotNav() {
    // Destroy Flickity if it was initialized
    if ($wrapper.hasClass('flickity-enabled')) {
      $wrapper.flickity('destroy');
      $wrapper.removeClass('flickity-enabled is-draggable');
      $wrapper.find('.flickity-viewport, .flickity-page-dots, .flickity-button').remove();
    }

    // Clean up inline styles left by Flickity
    var $items = $wrapper.find('.gallery__item');
    $items.removeAttr('style').removeClass('flickity-modified is-selected');
    $wrapper.removeAttr('style');

    if ($items.length < 2) return;

    // Build dots if not already present
    if (!$section.find('.lp-gallery-dots').length) {
      var dotsHtml = '<div class="lp-gallery-dots">';
      $items.each(function(i) {
        dotsHtml += '<button class="lp-dot' + (i === 0 ? ' active' : '') + '" data-index="' + i + '" aria-label="Show image ' + (i + 1) + '"></button>';
      });
      dotsHtml += '</div>';
      $wrapper.after(dotsHtml);
    }

    // Force-load all lazy images so there's no flash on swipe
    $items.find('img').each(function() {
      var $img = $(this);
      var dataSrc = $img.data('src');
      var dataSrcset = $img.data('srcset');
      if (dataSrc) { $img.attr('src', dataSrc); }
      if (dataSrcset) { $img.attr('srcset', dataSrcset); }
      $img.addClass('lazyloaded').removeClass('lazyload');
      // Also preload via Image object
      var src = $img.attr('src');
      if (src) { new Image().src = src; }
    });

    // Show one slide at a time
    showSlide(currentSlide, $items);
  }

  function showSlide(idx, $items) {
    if (!$items) $items = $wrapper.find('.gallery__item');
    $items.each(function(i) {
      if (i === idx) {
        $(this).attr('style', 'display:block !important; width:100% !important; max-width:100% !important; float:none !important; margin:0 !important; padding:0 20px !important; box-sizing:border-box !important;');
      } else {
        $(this).attr('style', 'position:absolute !important; left:-9999px !important; visibility:hidden !important; width:100% !important; max-width:100% !important;');
      }
    });
    $wrapper.css('position', 'relative');
    $section.find('.lp-dot').removeClass('active').eq(idx).addClass('active');
  }

  function resetDesktop() {
    $wrapper.find('.gallery__item').removeAttr('style');
  }

  var currentSlide = 0;

  // Use a short delay to let Flickity initialize first, then destroy it
  if (isMobile()) {
    setTimeout(function() { initDotNav(); }, 500);
  }

  // Dot click
  $section.on('click', '.lp-dot', function() {
    currentSlide = $(this).data('index');
    showSlide(currentSlide);
  });

  // Swipe support
  var touchStartX = 0;
  var touchEndX = 0;
  $wrapper[0].addEventListener('touchstart', function(e) {
    touchStartX = e.changedTouches[0].screenX;
  }, { passive: true });
  $wrapper[0].addEventListener('touchend', function(e) {
    touchEndX = e.changedTouches[0].screenX;
    var diff = touchStartX - touchEndX;
    var totalSlides = $wrapper.find('.gallery__item').length;
    if (Math.abs(diff) > 50) { // minimum swipe distance
      if (diff > 0 && currentSlide < totalSlides - 1) {
        currentSlide++;
        showSlide(currentSlide);
      } else if (diff < 0 && currentSlide > 0) {
        currentSlide--;
        showSlide(currentSlide);
      }
    }
  }, { passive: true });

  // Handle resize
  $(window).on('resize', function() {
    if (isMobile()) { initDotNav(); } else { resetDesktop(); }
  });
});

// ============================================================
// Audigo cart validation
//
// Runs AFTER the theme finishes its own cart update so there is never
// a concurrent updateView conflict (which caused the infinite spinner).
//
// Strategy: on delete / minus-button clicks, schedule a check 1.2 s
// later — by then the theme's AJAX + updateView have fully settled.
// If the cart has a subscription but no mic (iOS or Android), remove the
// sub and do one clean updateView.
//
// Also runs once on page load to catch any stale invalid cart state.
// ============================================================
var _audigoPendingValidation;

function audigoClearSubsWithoutMic() {
  fetch('/cart.js', { cache: 'no-store' })
    .then(function(r) { return r.json(); })
    .then(function(cart) {
      // Either mic carries the Studio+ trial (iOS mic variants, or the Android mic)
      var _iosMap = window.AUDIGO_IOS_MIC_MAP || {};
      var _iosVariantIds = Object.keys(_iosMap).map(function(k) { return parseInt(_iosMap[k]); });
      var hasMic = cart.items.some(function(item) {
        if (item.selling_plan_allocation) { return false; }
        return _iosVariantIds.indexOf(item.variant_id) !== -1 || item.handle === 'wireless-mic-and-android-app';
      });
      var subItems = cart.items.filter(function(item) {
        return !!item.selling_plan_allocation;
      });
      console.log('[audigoClear] hasMic:', hasMic, '| subItems:', subItems.length, subItems.map(function(i) { return i.key + ' qty=' + i.quantity; }));
      var updates = {};
      if (!hasMic && subItems.length > 0) {
        // Rule 1: no mic — remove all subscriptions
        subItems.forEach(function(item) { updates[item.key] = 0; });
      } else if (subItems.length > 1) {
        // Cap: more than 1 subscription line item — remove all but the first
        subItems.slice(1).forEach(function(item) { updates[item.key] = 0; });
      } else if (subItems.length === 1 && subItems[0].quantity > 1) {
        // Cap: subscription quantity somehow incremented — force it back to 1
        updates[subItems[0].key] = 1;
      }
      if (Object.keys(updates).length > 0) {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function() {
          window._audigoSpinnerSub = false;
          Shopify.theme.jsAjaxCart.updateView();
        }).catch(function(err) {
          window._audigoSpinnerSub = false;
          console.warn('[Audigo] Could not remove orphan subscription:', err);
        });
      } else if (window._audigoSpinnerSub) {
        // Another plan-eligible mic is still in the cart, so the sub stays — drop its spinner
        window._audigoSpinnerSub = false;
        $('.ajax-cart__product[data-is-subscription] .ajax-cart__qty-delete').removeClass('is-updating');
      }
    })
    .catch(function(err) {
      console.warn('[Audigo] Cart validation fetch failed:', err);
    });
}

// True when the given cart row is the only mic (iOS or Android) left, i.e. removing
// it will cascade-delete the Studio+ sub. Used to decide whether to spinner the sub.
function audigoIsLastPlanMic($row) {
  return $('.ajax-cart__product[data-is-ios-mic], .ajax-cart__product[data-is-android-mic]').not($row).length === 0;
}

function scheduleAudigoCartValidation() {
  clearTimeout(_audigoPendingValidation);
  _audigoPendingValidation = setTimeout(audigoClearSubsWithoutMic, 800);
}

// Trigger after delete button or minus-button clicks.
// 800ms ensures the theme's own AJAX + updateView have fully settled.
$(document).on('click', '[data-ajax-cart-delete]', function () {
  // Show spinner on the clicked delete button itself
  var $btn = $(this);
  if ($btn.hasClass('ajax-cart__qty-delete')) {
    $btn.addClass('is-updating');
  }

  // If deleting the last mic, also spinner the subscription's delete button
  var $item = $btn.closest('[data-is-ios-mic], [data-is-android-mic]');
  if ($item.length && audigoIsLastPlanMic($item)) {
    window._audigoSpinnerSub = true;
    $('.ajax-cart__product[data-is-subscription] .ajax-cart__qty-delete').addClass('is-updating');
  }
  scheduleAudigoCartValidation();
});
$(document).on('click', '.ajax-cart [data-update-quantity]', scheduleAudigoCartValidation);

// Run once on load to catch stale cart state from a previous session.
$(document).ready(function() {
  setTimeout(audigoClearSubsWithoutMic, 2000);
});

// ============================================================
// Cart upsell — "Add to order" (Studio+ free trial)
// Reads subscription config from the product ATC button in the DOM.
// TODO: promote subscription_variant_id + selling_plan_id to global
// theme settings so this works on all pages, not just product pages.
// ============================================================
$(document).on('click', '.ajax-cart__studio-add-btn', function() {
  // Read IDs from cart form (set via global theme settings in cart.ajax.liquid).
  // Fall back to product ATC button when on product page.
  var $cartForm = $('.ajax-cart__form');
  var $atcBtn = $('.product-a-atc[data-subscription-variant-id], .product-b-atc[data-subscription-variant-id]').first();
  var subVariantId = $cartForm.data('sub-variant-id') || $atcBtn.data('subscription-variant-id');
  var sellingPlanId = $cartForm.data('sub-selling-plan-id') || $atcBtn.data('selling-plan-id');

  if (!subVariantId || !sellingPlanId) {
    console.warn('[Audigo] Studio+ upsell: missing variant/selling-plan IDs.');
    return;
  }

  var $btn = $(this).text('Adding...').prop('disabled', true);

  // Guard: don't add a second subscription if one already exists
  fetch('/cart.js')
    .then(function(r) { return r.json(); })
    .then(function(cart) {
      var hasExistingSub = cart.items.some(function(i) { return !!i.selling_plan_allocation; });
      if (hasExistingSub) {
        $btn.text('Add to order').prop('disabled', false);
        Shopify.theme.jsAjaxCart.updateView();
        return;
      }
      doStudioUpsellAdd($btn, subVariantId, sellingPlanId);
    });
});

function doStudioUpsellAdd($btn, subVariantId, sellingPlanId) {
  $.ajax({
    type: 'POST',
    url: '/cart/add.js',
    dataType: 'json',
    data: {
      items: [{
        id: subVariantId,
        quantity: 1,
        selling_plan: sellingPlanId,
        properties: {
          _sub_len: '1',
          _sub_period: 'month',
          _sub_has_trial: 'true',
          _sub_trial_len: '30',
          _sub_trial_period: 'days',
          _sub_price: '15',
          _sub_spid: String(sellingPlanId)
        }
      }]
    },
    success: function() {
      Shopify.theme.jsAjaxCart.updateView();
    },
    error: function(err) {
      console.error('[Audigo] Could not add Studio+ to cart:', err.responseJSON || err);
      $btn.text('Add to order').prop('disabled', false);
    }
  });
}

// ============================================================
// A/B Test: cart-plan-choice
// Test arm gets monthly/annual radio options under the Studio+ line item in the
// cart drawer; control keeps today's single auto-attached monthly plan.
//
// The markup renders for everyone (see snippets/cart-plan-select.liquid) but is
// display:none until this adds `audigo-cart-plan-choice` to <body>, so the control
// arm never flashes the selector while flags resolve.
//
// Selecting an option mutates the cart rather than deferring to checkout: the line
// item's own title, plan name and price are Shopify's, so the row can only tell the
// truth about what was picked if the cart actually holds that plan.
// ============================================================
(function initCartPlanChoice() {
  var evaluated = false;
  var flagsReady = false;

  function enable() {
    document.body.classList.add('audigo-cart-plan-choice');
    if (window.posthog && typeof posthog.capture === 'function') {
      posthog.capture('cart_plan_choice_enrolled', {}, { send_feature_flags: true });
    }
  }

  // QA override so the arm can be reviewed on a staging theme before the PostHog flag
  // exists, mirroring the ?test_android=true convention used elsewhere in this file.
  // Persisted for the session because the cart drawer is reached from other pages.
  var FORCE_RE = /[?&]cart_plan_choice=(test|true|1)\b/i;
  function forcedOn() {
    try {
      if (FORCE_RE.test(window.location.search)) {
        sessionStorage.setItem('audigo_cart_plan_choice', 'test');
      }
      return sessionStorage.getItem('audigo_cart_plan_choice') === 'test';
    } catch (e) {
      return FORCE_RE.test(window.location.search);
    }
  }

  // The selector's markup renders for BOTH arms (it is display:none until enabled), so
  // its presence means "this visitor is eligible" and nothing more. Enrolment is scoped
  // to that moment rather than to page load: evaluating on every page would enrol
  // Android visitors, non-US visitors and everyone who never opens a cart, diluting the
  // experiment with people who could not have seen the thing being tested. Same reason
  // the former ios-pdp-no-subscription test scoped itself to the US iOS PDP.
  function eligible() {
    return !!document.querySelector('[data-plan-select]');
  }

  function evaluateIfEligible() {
    if (evaluated || !eligible()) return;
    evaluated = true;
    if (forcedOn()) { enable(); return; }
    if (typeof posthog === 'undefined' || typeof posthog.getFeatureFlag !== 'function') return;
    // getFeatureFlag is what records exposure, so it is deliberately called here and
    // nowhere else.
    if (posthog.getFeatureFlag('cart-plan-choice') === 'test') enable();
  }

  // Warm the flags early so that, once the drawer does render, the arm resolves without
  // a visible delay. onFeatureFlags does not itself record an exposure.
  function warm() {
    if (typeof posthog === 'undefined' || typeof posthog.onFeatureFlags !== 'function') return;
    posthog.onFeatureFlags(function () {
      flagsReady = true;
      evaluateIfEligible();
    });
  }

  function start() {
    warm();
    evaluateIfEligible();
    // The drawer is rendered client-side and re-rendered on every cart change.
    if (typeof MutationObserver === 'function') {
      new MutationObserver(function () { evaluateIfEligible(); })
        .observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

// Subscription line-item properties consumed by the confirmation emails. Read off the
// option in the DOM, which Liquid derived from the plan's own price adjustments, rather
// than from a map here that cannot know which plan is configured. A hardcoded map had
// the prepaid annual plan claiming a 30-day trial it does not have.
function audigoPlanProps($btn, planId) {
  var props = {
    _sub_len: String($btn.data('sub-len') || '1'),
    _sub_period: String($btn.data('sub-period') || ''),
    _sub_has_trial: String($btn.data('sub-has-trial')) === 'true' ? 'true' : 'false',
    _sub_price: String($btn.data('sub-price') || ''),
    _sub_spid: String(planId)
  };
  if (props._sub_has_trial === 'true') {
    props._sub_trial_len = String($btn.data('sub-trial-len') || '');
    props._sub_trial_period = String($btn.data('sub-trial-period') || 'days');
  }
  return props;
}

// Quiet decline: detach Studio+ and leave checkout live. The mic is untouched, so the
// cart stays valid and the checkout button is never disabled.
$(document).on('click', '[data-plan-decline]', function () {
  var $btn = $(this);
  var $group = $btn.closest('[data-plan-select]');
  if ($group.hasClass('is-updating')) return;
  var itemKey = String($btn.data('cart-item-key') || '');
  if (!itemKey) return;

  // Busy treatment on the whole section rather than a single card, because declining
  // acts on all of Studio+ rather than on one plan.
  $group.addClass('is-updating is-declining');
  if (window.posthog && typeof posthog.capture === 'function') {
    posthog.capture('cart_plan_declined', {}, { send_feature_flags: true });
  }

  $.ajax({
    type: 'POST', url: '/cart/change.js', dataType: 'json',
    data: { id: itemKey, quantity: 0 }
  })
    .always(function () {
      Shopify.theme.jsAjaxCart.updateView();
      if (typeof window.audigoRefreshCartBadge === 'function') window.audigoRefreshCartBadge();
      $group.removeClass('is-updating is-declining');
    });
});

$(document).on('click', '.ajax-cart__plan-option', function () {
  var $btn = $(this);
  if ($btn.hasClass('is-selected')) return;

  var $group = $btn.closest('[data-plan-select]');
  if ($group.hasClass('is-updating')) return;

  var planKey = $btn.data('plan-option');
  var planId = String($btn.data('plan-id') || '');
  var variantId = String($btn.data('variant-id') || '');
  var itemKey = String($group.data('cart-item-key') || '');

  if (!planId || !variantId || !itemKey) {
    console.warn('[Audigo] cart-plan-choice: missing plan/variant/item key.');
    return;
  }

  // Show the same busy state the mic quantity controls use: grey the group and spin a
  // centred indicator on the card that was clicked, letting the selection move when the
  // drawer re-renders. Moving the radio optimistically and then sitting still for a
  // second reads worse than not moving it at all, because the control appears to have
  // finished when it has not.
  $group.addClass('is-updating');
  $btn.addClass('is-pending');

  if (window.posthog && typeof posthog.capture === 'function') {
    posthog.capture('cart_plan_selected', { plan: planKey }, { send_feature_flags: true });
  }

  var newSub = {
    id: variantId, quantity: 1, selling_plan: planId,
    properties: audigoPlanProps($btn, planId)
  };

  fetch('/cart.js', { cache: 'no-store' })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      var items = (cart && cart.items) || [];
      var others = items.filter(function (i) { return !i.selling_plan_allocation; });

      // Drop every subscription line plus the lines being reordered, in one write.
      var updates = {};
      items.forEach(function (i) {
        if (i.selling_plan_allocation || others.indexOf(i) !== -1) updates[i.key] = 0;
      });

      // Reversed, because each element of the array prepends in turn.
      var readd = [newSub].concat(others.slice().reverse().map(function (i) {
        var o = { id: i.variant_id, quantity: i.quantity };
        if (i.properties && Object.keys(i.properties).length) o.properties = i.properties;
        return o;
      }));

      return $.ajax({
        type: 'POST', url: '/cart/update.js', dataType: 'json', data: { updates: updates }
      }).then(function () {
        return $.ajax({
          type: 'POST', url: '/cart/add.js', dataType: 'json', data: { items: readd }
        }).catch(function (err) {
          // The update already emptied those lines, so a failed add leaves the cart
          // short. Retry once before giving up.
          console.warn('[Audigo] cart-plan-choice: re-add failed, retrying', err);
          return $.ajax({
            type: 'POST', url: '/cart/add.js', dataType: 'json', data: { items: readd }
          });
        });
      });
    })
    .then(function () {
      Shopify.theme.jsAjaxCart.updateView();
      if (typeof window.audigoRefreshCartBadge === 'function') window.audigoRefreshCartBadge();
    })
    .catch(function (err) {
      console.error('[Audigo] cart-plan-choice swap failed:', (err && err.responseJSON) || err);
      // Re-render so the drawer shows the cart as it actually is rather than the
      // optimistic selection.
      Shopify.theme.jsAjaxCart.updateView();
    })
    .then(function () {
      $group.removeClass('is-updating');
    });
});

// ============================================================
// A/B Test: cart-gift-checkbox
// Test arm gets a "This is a gift" checkbox at the foot of the cart drawer's item
// list; control sees nothing new. Checking it detaches Studio+ from the cart and
// records the intent. Unchecking clears the intent but deliberately does NOT put the
// subscription back — re-adding a plan the buyer just took off would undo a choice
// they made, and the whole point of this arm is to observe the choice, not steer it.
//
// This is phase 1 of the gifting flow. The prepaid year and the emailed gift code are
// not built here; all this arm measures is whether people reach for the box, so it can
// be compared against the share of buyers who tell the post-purchase survey the order
// was a gift.
//
// The markup renders for everyone (see snippets/cart-gift-check.liquid) but is
// display:none until this adds `audigo-cart-gift-checkbox` to <body>, so the control
// arm never flashes the box while flags resolve. Same shape as cart-plan-choice above.
// ============================================================
(function initCartGiftCheckbox() {
  var evaluated = false;

  function enable() {
    document.body.classList.add('audigo-cart-gift-checkbox');
    if (window.posthog && typeof posthog.capture === 'function') {
      posthog.capture('cart_gift_enrolled', {}, { send_feature_flags: true });
    }
  }

  // QA override so the arm can be reviewed on a staging theme before the PostHog flag
  // exists, mirroring ?cart_plan_choice=test and ?test_android=true. Persisted for the
  // session because the drawer is reached from pages other than the one carrying the param.
  var FORCE_RE = /[?&]cart_gift=(test|true|1)\b/i;
  function forcedOn() {
    try {
      if (FORCE_RE.test(window.location.search)) {
        sessionStorage.setItem('audigo_cart_gift', 'test');
      }
      return sessionStorage.getItem('audigo_cart_gift') === 'test';
    } catch (e) {
      return FORCE_RE.test(window.location.search);
    }
  }

  // The block renders for BOTH arms (display:none until enabled), so its presence means
  // "this visitor is eligible" and nothing more. Enrolment is scoped to that moment
  // rather than to page load: evaluating everywhere would enrol non-US visitors and
  // everyone who never opens a cart, diluting the experiment with people who could not
  // have seen the thing being tested.
  function eligible() {
    return !!document.querySelector('[data-gift-block]');
  }

  function evaluateIfEligible() {
    if (evaluated || !eligible()) return;
    evaluated = true;
    if (forcedOn()) { enable(); return; }
    if (typeof posthog === 'undefined' || typeof posthog.getFeatureFlag !== 'function') return;
    // getFeatureFlag is what records exposure, so it is deliberately called here and
    // nowhere else.
    if (posthog.getFeatureFlag('cart-gift-checkbox') === 'test') enable();
  }

  // Warm the flags early so the arm resolves without a visible delay once the drawer
  // renders. onFeatureFlags does not itself record an exposure.
  function warm() {
    if (typeof posthog === 'undefined' || typeof posthog.onFeatureFlags !== 'function') return;
    posthog.onFeatureFlags(function () { evaluateIfEligible(); });
  }

  function start() {
    warm();
    evaluateIfEligible();
    // The drawer is rendered client-side and re-rendered on every cart change.
    if (typeof MutationObserver === 'function') {
      new MutationObserver(function () { evaluateIfEligible(); })
        .observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

$(document).on('change', '[data-gift-check]', function () {
  var $input = $(this);
  var $block = $input.closest('[data-gift-block]');
  if ($block.hasClass('is-updating')) return;
  var checked = $input.is(':checked');

  $block.addClass('is-updating');

  // Removing the subscription and recording the intent go out as ONE /cart/update.js.
  // The identifier-stamping block above spells out why: a second concurrent write into
  // this theme's cart sequences clobbers line-item changes. update.js takes `updates`
  // and `attributes` together, so one write is all this needs. An empty-string
  // attribute value is a delete, which is how unchecking clears the flag.
  var payload = { attributes: { '_is_gift': checked ? 'true' : '' } };

  var prepared = checked
    ? fetch('/cart.js', { cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var subs = ((cart && cart.items) || []).filter(function (i) {
            return !!i.selling_plan_allocation;
          });
          if (subs.length) {
            payload.updates = {};
            subs.forEach(function (i) { payload.updates[i.key] = 0; });
          }
          return subs.length;
        })
    : Promise.resolve(0);

  prepared
    .catch(function () { return 0; })  // cart read failed; still record the intent
    .then(function (removedSubs) {
      // Captured on the toggle rather than on the write's success: the metric this arm
      // exists to read is whether people reach for the box, and they did that whether or
      // not the cart write then landed.
      if (window.posthog && typeof posthog.capture === 'function') {
        posthog.capture(checked ? 'cart_gift_checked' : 'cart_gift_unchecked',
          { removed_subscription: removedSubs > 0 }, { send_feature_flags: true });
      }
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    })
    .catch(function (err) {
      console.warn('[Audigo] cart-gift-checkbox: update failed', err);
    })
    .then(function () {
      // Re-render either way, so the box reflects the cart as it actually is rather
      // than the browser's optimistic toggle.
      Shopify.theme.jsAjaxCart.updateView();
      if (typeof window.audigoRefreshCartBadge === 'function') window.audigoRefreshCartBadge();
    })
    // Separate link so the block never strands in its busy state if the re-render above
    // throws, matching how the plan selector clears its own.
    .then(function () { $block.removeClass('is-updating'); },
          function () { $block.removeClass('is-updating'); });
});

// Final gift state, captured at the Checkout click — the one moment that decides what the
// order is. cart_gift_checked only says the box was ticked at some point; someone who ticks
// and then unticks would otherwise count as a gift.
//
// Deliberately a second handler rather than an edit to the identifier-stamping one above,
// which cancels the click, awaits a cart write and re-clicks. That sequence is delicate and
// worth leaving alone. This one is safe beside it: the stamping handler uses
// stopPropagation, not stopImmediatePropagation, so same-node handlers still run; and this
// handler never cancels the event, never returns false and never awaits, so it cannot delay
// or block the submit. The whole body is wrapped because jQuery does not catch handler
// exceptions, and a throw here would stop any handler registered after it.
//
// Reading `checked` off the DOM gets the state Liquid rendered from the cart attribute, so
// it is the cart's own answer rather than a replay of toggle events. If the event is lost on
// unload, `_is_gift` is still on the order, so the count is recoverable from Shopify.
$(document).on('click', 'button[name="checkout"]', function () {
  try {
    // The stamping handler re-clicks this same node to let the form submit, so guard
    // against logging twice. Toggling the box re-renders the drawer and replaces the
    // button, which clears the flag — a changed mind after a cancelled checkout still logs.
    if (this._audigoGiftLogged) return;
    // Test arm only. The box renders for both arms and is merely display:none in control,
    // so its presence in the DOM does not mean the visitor could see it — gate on the class
    // that enrolment adds, or control checkouts land in the exposed denominator too.
    if (!document.body.classList.contains('audigo-cart-gift-checkbox')) return;
    var box = document.querySelector('[data-gift-check]');
    if (!box) return;  // not eligible
    this._audigoGiftLogged = true;
    if (window.posthog && typeof posthog.capture === 'function') {
      posthog.capture('cart_gift_checkout', { is_gift: box.checked }, { send_feature_flags: true });
    }
  } catch (e) {
    console.warn('[Audigo] cart-gift-checkbox: checkout capture failed', e);
  }
});

// ============================================================
// DISABLED FOR RELEASE: Clear cart helper
// ============================================================
// $(document).on('click', '.dev-clear-cart', function (e) {
//   e.preventDefault();
//   e.stopPropagation();
//   $.post('/cart/clear.js', function () {
//     Shopify.theme.jsAjaxCart.updateView();
//   });
// });

// ========================================
// Audigo Country Selector Component
// ========================================
class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="country_code"]'),
      button: this.querySelector('.country-selector__button'),
      closeButton: this.querySelector('.country-selector__close'),
      dropdown: this.querySelector('.country-selector__dropdown'),
    };
    
    this.elements.button.addEventListener('click', this.toggleSelector.bind(this));
    this.elements.closeButton.addEventListener('click', this.closeSelector.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
    
    this.querySelectorAll('.country-selector__link').forEach(item => {
      item.addEventListener('click', this.onItemClick.bind(this));
    });
    
    // Close on outside click
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target) && !this.elements.dropdown.hasAttribute('hidden')) {
        this.closeSelector();
      }
    });
  }

  toggleSelector() {
    const isHidden = this.elements.dropdown.hasAttribute('hidden');
    if (isHidden) {
      this.openSelector();
    } else {
      this.closeSelector();
    }
  }

  openSelector() {
    this.elements.dropdown.removeAttribute('hidden');
    this.elements.button.setAttribute('aria-expanded', 'true');
    this.elements.button.classList.add('is-open');
  }

  closeSelector() {
    this.elements.dropdown.setAttribute('hidden', '');
    this.elements.button.setAttribute('aria-expanded', 'false');
    this.elements.button.classList.remove('is-open');
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() === 'ESCAPE') {
      this.closeSelector();
      this.elements.button.focus();
    }
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }
}

// Register the custom element
if (typeof customElements !== 'undefined' && !customElements.get('localization-form')) {
  customElements.define('localization-form', LocalizationForm);
}

// ============================================================
// Floating "Shop Now" button during pinned scrollytelling sections
// ============================================================
(function() {
  // Wait for ScrollTrigger to initialize
  var checkInterval = setInterval(function() {
    if (typeof ScrollTrigger === 'undefined') return;
    var triggers = ScrollTrigger.getAll();
    if (triggers.length === 0) return;
    clearInterval(checkInterval);
    initFloatingCTA(triggers);
  }, 500);
  setTimeout(function() { clearInterval(checkInterval); }, 10000);

  function initFloatingCTA() {
    // Create the floating button
    var btn = document.createElement('a');
    btn.href = window.isAndroid ? '/products/wireless-mic-and-android-app' : '/products/wireless-mic-and-ios-app';
    btn.textContent = 'Shop now';
    btn.className = 'floating-shop-cta';
    btn.style.cssText =
      'position:fixed;bottom:40px;left:50%;transform:translateX(-50%) translateY(20px);z-index:50;'
      + 'display:inline-flex;align-items:center;justify-content:center;white-space:nowrap;'
      + 'font-family:source-sans-pro,sans-serif;font-size:16px;font-weight:600;letter-spacing:0.5px;'
      + 'color:#FFFFFF;background:#131313;border-radius:26px;height:52px;padding:0 64px;'
      + 'text-decoration:none;opacity:0;pointer-events:none;'
      + 'transition:opacity 0.4s ease,transform 0.4s ease;';
    document.body.appendChild(btn);

    if (!('ontouchstart' in window)) {
      btn.addEventListener('mouseenter', function() { btn.style.opacity = '0.85'; });
      btn.addEventListener('mouseleave', function() {
        if (btn.dataset.visible === 'true') btn.style.opacity = '1';
      });
    }

    var isVisible = false;

    function showCTA() {
      if (isVisible) return;
      isVisible = true;
      btn.dataset.visible = 'true';
      btn.style.opacity = '1';
      btn.style.pointerEvents = 'auto';
      btn.style.transform = 'translateX(-50%) translateY(0)';
    }

    function hideCTA() {
      if (!isVisible) return;
      isVisible = false;
      btn.dataset.visible = 'false';
      btn.style.opacity = '0';
      btn.style.pointerEvents = 'none';
      btn.style.transform = 'translateX(-50%) translateY(20px)';
    }

    // On scroll, check if any pinned section is active.
    // Debounce the hide to prevent flicker during snap transitions.
    var hideTimeout = null;
    window.addEventListener('scroll', function() {
      var triggers = ScrollTrigger.getAll();
      var anyPinActive = false;
      var scrollY = window.scrollY;
      for (var i = 0; i < triggers.length; i++) {
        var t = triggers[i];
        if (!t.pin) continue;
        // isActive is false at exact start/end boundaries — include them
        if (t.isActive || (scrollY >= t.start && scrollY <= t.end)) {
          anyPinActive = true;
          break;
        }
      }
      if (anyPinActive) {
        if (hideTimeout) { clearTimeout(hideTimeout); hideTimeout = null; }
        showCTA();
      } else {
        // Delay hiding to survive snap-induced flickers
        if (!hideTimeout) {
          hideTimeout = setTimeout(function() {
            hideCTA();
            hideTimeout = null;
          }, 300);
        }
      }
    }, { passive: true });
  }
})();

// ============================================================
// DEBUG: Scroll progress widget (activate with ?debug_scroll)
// ============================================================
(function() {
  if (window.location.search.indexOf('debug_scroll') === -1) return;

  // Wait for ScrollTrigger to be available and sections to initialize
  var checkInterval = setInterval(function() {
    if (typeof ScrollTrigger === 'undefined') return;
    var triggers = ScrollTrigger.getAll();
    if (triggers.length === 0) return;
    clearInterval(checkInterval);
    buildWidget(triggers);
  }, 500);

  // Stop checking after 10s
  setTimeout(function() { clearInterval(checkInterval); }, 10000);

  function buildWidget(triggers) {
    var widget = document.createElement('div');
    widget.id = 'debug-scroll-widget';
    widget.style.cssText = 'position:fixed;top:12px;right:12px;z-index:99999;'
      + 'background:rgba(0,0,0,0.85);color:#0f0;font:12px/1.4 monospace;'
      + 'padding:8px 12px;border-radius:8px;pointer-events:none;'
      + 'max-width:280px;backdrop-filter:blur(4px);';
    document.body.appendChild(widget);

    // Label each trigger by its trigger element
    function label(t) {
      var el = t.trigger;
      if (!el) return 'unknown';
      if (el.id) return '#' + el.id;
      var cls = el.className;
      if (typeof cls === 'string' && cls.trim()) {
        // Use the first meaningful class
        var first = cls.trim().split(/\s+/)[0];
        return '.' + first;
      }
      return el.tagName.toLowerCase();
    }

    function update() {
      var lines = ['<b style="color:#fff">Scroll Debug</b>'];
      var allTriggers = ScrollTrigger.getAll();
      allTriggers.forEach(function(t) {
        var p = (t.progress * 100).toFixed(1);
        var active = t.isActive;
        var color = active ? '#0f0' : '#888';
        lines.push(
          '<span style="color:' + color + '">'
          + label(t) + ': ' + p + '%'
          + (active ? ' ●' : '')
          + '</span>'
        );
      });
      // Also show page scroll %
      var pageScroll = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight) * 100;
      lines.push('<span style="color:#ff0">page: ' + pageScroll.toFixed(1) + '%</span>');
      widget.innerHTML = lines.join('<br>');
      requestAnimationFrame(update);
    }
    update();
  }
})();

// ============================================================
// iOS mic cart — variant-swap qty controls
// ============================================================

/**
 * Swap the iOS mic variant in cart to reflect a new mic count.
 * Uses add-before-remove so a mic is always present during the
 * operation (keeping audigoClearSubsWithoutMic from firing early).
 *
 * @param {string} itemKey      - Cart item key of the current iOS mic line
 * @param {number} currentMicCount - Displayed mic count (from mic_unit_count metafield × qty)
 * @param {string} direction    - 'plus' or 'minus'
 */
function swapIosMicVariant(itemKey, currentMicCount, direction, map, maxMics) {
  map = map || window.AUDIGO_IOS_MIC_MAP;
  maxMics = maxMics || window.AUDIGO_IOS_MIC_MAX || 4;
  if (!map) { return; }

  var targetMicCount = currentMicCount + (direction === 'plus' ? 1 : -1);

  // Minus at 1 → remove the item entirely
  if (targetMicCount < 1) {
    // Spinner the subscription's delete button if a cascade delete will follow
    if (audigoIsLastPlanMic($('.ajax-cart__product[data-cart-item="' + itemKey + '"]'))) {
      window._audigoSpinnerSub = true;
      $('.ajax-cart__product[data-is-subscription] .ajax-cart__qty-delete').addClass('is-updating');
    }

    var _rem = {};
    _rem[itemKey] = 0;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: _rem })
    })
    .then(function() {
      Shopify.theme.jsAjaxCart.updateView();
      scheduleAudigoCartValidation();
    })
    .catch(function(err) {
      console.error('[Audigo] iOS mic remove error:', err);
    });
    return;
  }

  // Recovery: if display_qty > maxMics (e.g. qty got set to 2 on a 4-pack → shows 8),
  // pressing minus should reset the line item qty to 1, restoring a valid state.
  if (currentMicCount > maxMics) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity: 1 })
    })
    .then(function() {
      Shopify.theme.jsAjaxCart.updateView();
      scheduleAudigoCartValidation();
    })
    .catch(function(err) {
      console.error('[Audigo] iOS mic qty reset error:', err);
      Shopify.theme.jsAjaxCart.updateView();
    });
    return;
  }

  if (targetMicCount > maxMics) { return; }

  var newVariantId = parseInt(map[String(targetMicCount)]);
  if (!newVariantId) {
    console.warn('[Audigo] No iOS mic variant for count:', targetMicCount);
    return;
  }

  // 1. Add new variant → 2. Remove old by key → 3. updateView
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: newVariantId, quantity: 1 }] })
  })
  .then(function(r) { return r.json(); })
  .then(function() {
    var _rem = {};
    _rem[itemKey] = 0;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: _rem })
    });
  })
  .then(function() {
    Shopify.theme.jsAjaxCart.updateView();
    scheduleAudigoCartValidation();
  })
  .catch(function(err) {
    console.error('[Audigo] iOS mic swap error:', err);
    Shopify.theme.jsAjaxCart.updateView();
  });
}

$(document).on('click', '[data-ios-mic-minus]', function () {
  var $pill = $(this).closest('.ajax-cart__qty-pill');
  $pill.addClass('is-updating');
  var map = null, maxMics = null;
  try { var m = $pill.attr('data-ios-mic-map'); if (m) { map = JSON.parse(m); } maxMics = parseInt($pill.attr('data-ios-mic-max'), 10) || null; } catch(e) {}
  var itemKey    = $(this).attr('data-ios-mic-minus');
  var displayQty = parseInt($(this).attr('data-display-qty'), 10);
  swapIosMicVariant(itemKey, displayQty, 'minus', map, maxMics);
});

$(document).on('click', '[data-ios-mic-plus]:not(.is-disabled)', function () {
  var $pill = $(this).closest('.ajax-cart__qty-pill');
  $pill.addClass('is-updating');
  var map = null, maxMics = null;
  try { var m = $pill.attr('data-ios-mic-map'); if (m) { map = JSON.parse(m); } maxMics = parseInt($pill.attr('data-ios-mic-max'), 10) || null; } catch(e) {}
  var itemKey    = $(this).attr('data-ios-mic-plus');
  var displayQty = parseInt($(this).attr('data-display-qty'), 10);
  swapIosMicVariant(itemKey, displayQty, 'plus', map, maxMics);
});

// Greyed-out + button: block the theme's qty handler and reveal the max-qty info message
$(document).on('click', '.ajax-cart .quantity-plus.is-disabled', function (e) {
  e.stopImmediatePropagation();
  e.preventDefault();
  $(this).closest('.ajax-cart__product').siblings('.ajax-cart__item-info.is-max-qty-msg').addClass('is-revealed');
});

// After cart DOM re-renders, apply pending visual states
(function () {
  var observer = new MutationObserver(function () {
    if (window._audigoRevealMaxQtyMsg) {
      // Keep the flag alive across multiple updateView() DOM replacements.
      // Clear it on a 2s timer so late re-renders still pick it up.
      setTimeout(function () {
        $('.ajax-cart__item-info.is-max-qty-msg').addClass('is-revealed');
      }, 50);
      clearTimeout(window._audigoRevealMaxQtyTimer);
      window._audigoRevealMaxQtyTimer = setTimeout(function () {
        window._audigoRevealMaxQtyMsg = false;
      }, 2000);
    }
    if (window._audigoAndroidMaxMsg) {
      setTimeout(function () {
        var $androidItem = $('.ajax-cart__product[data-is-android-mic]');
        if ($androidItem.length && !$androidItem.next('.ajax-cart__item-info--android-max').length) {
          var msgHtml = '<div class="ajax-cart__item-info ajax-cart__item-info--android-max" style="order:' + $androidItem.css('order') + ';">' +
            '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<circle cx="10" cy="10" r="8" stroke="#979797" stroke-width="1.5"/>' +
            '<path d="M10 9v5" stroke="#979797" stroke-width="1.5" stroke-linecap="round"/>' +
            '<circle cx="10" cy="6.5" r="0.75" fill="#979797"/>' +
            '</svg>' +
            '<span>Maximum quantity reached. Android multimic support coming soon.</span>' +
            '</div>';
          $androidItem.after(msgHtml);
        }
      }, 50);
      clearTimeout(window._audigoAndroidMaxTimer);
      window._audigoAndroidMaxTimer = setTimeout(function () {
        window._audigoAndroidMaxMsg = false;
      }, 2000);
    }
    if (window._audigoSpinnerSub) {
      // Re-apply spinner on subscription delete button after updateView replaced the DOM
      setTimeout(function () {
        var $subBtn = $('.ajax-cart__product[data-is-subscription] .ajax-cart__qty-delete');
        if ($subBtn.length) {
          $subBtn.addClass('is-updating');
        } else {
          // Subscription already removed — clear the flag
          window._audigoSpinnerSub = false;
        }
      }, 50);
    }
  });
  var target = document.getElementById('theme-ajax-cart');
  if (target) observer.observe(target, { childList: true, subtree: true });
}());

// ── Accurate cart badge count ──────────────────────────────────────────────
// The theme uses cart.item_count (line-item count) for the header badge, but
// iOS mic variants represent multiple physical mics as a single line item.
// This refresh reads AUDIGO_IOS_MIC_MAP (set in theme.liquid) and updates the
// header badge to show the correct display count after every cart change.
(function () {
  // Build inverted map: variantId (string) → micCount
  function getMicMap() {
    var map = {};
    if (window.AUDIGO_IOS_MIC_MAP) {
      Object.keys(window.AUDIGO_IOS_MIC_MAP).forEach(function (cnt) {
        map[String(window.AUDIGO_IOS_MIC_MAP[cnt])] = parseInt(cnt, 10);
      });
    }
    return map;
  }

  function computeDisplayCount(items, micMap) {
    var count = 0;
    items.forEach(function (item) {
      var cnt = micMap[String(item.variant_id)];
      count += (cnt !== undefined) ? cnt : item.quantity;
    });
    return count;
  }

  // Cache the last known correct count so we can suppress the theme's
  // wrong value (line-item count) until the fresh fetch resolves.
  var _lastCorrectCount = null;
  var BADGE_CACHE_KEY = 'audigo_cart_badge_count';

  function refreshHeaderBadge() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = computeDisplayCount(cart.items, getMicMap());
        _lastCorrectCount = count;
        try { localStorage.setItem(BADGE_CACHE_KEY, String(count)); } catch (e) {}
        setBadge(count);
      })
      .catch(function () {});
  }

  function setBadge(count) {
    document.querySelectorAll(
      '.header-cart__count--badge[data-bind="itemCount"],' +
      ' .header-cart__count--text[data-bind="itemCount"]'
    ).forEach(function (el) {
      el.textContent = count;
      el.classList.add('is-ready');
    });
    document.querySelectorAll('.header-cart[data-ajax-cart-trigger]').forEach(function (el) {
      el.classList.toggle('has-cart-count', count > 0);
    });
  }

  // Render cached count immediately so the badge doesn't flicker off across
  // page navigations. The async fetch below will correct it if cart changed.
  try {
    var cached = localStorage.getItem(BADGE_CACHE_KEY);
    if (cached !== null) {
      var cachedCount = parseInt(cached, 10);
      if (!isNaN(cachedCount)) {
        _lastCorrectCount = cachedCount;
        setBadge(cachedCount);
      }
    }
  } catch (e) {}

  window.audigoRefreshCartBadge = refreshHeaderBadge;

  // Run on page load (fixes server-rendered item_count)
  refreshHeaderBadge();

  // Watch badge elements directly — when the theme writes a wrong count,
  // immediately restore the cached count, then kick off a fresh fetch.
  var _badgeFetchTimer = null;
  document.querySelectorAll('[data-bind="itemCount"]').forEach(function (el) {
    new MutationObserver(function () {
      if (_lastCorrectCount !== null && el.textContent !== String(_lastCorrectCount)) {
        // Theme just wrote the line-item count — overwrite immediately
        setBadge(_lastCorrectCount);
        // Then fetch fresh data in case the cart actually changed
        clearTimeout(_badgeFetchTimer);
        _badgeFetchTimer = setTimeout(refreshHeaderBadge, 300);
      }
    }).observe(el, { childList: true, characterData: true, subtree: true });
  });

  // Also re-run after cart content re-render as a safety net
  var cartContent = document.getElementById('ajax-cart__content');
  if (cartContent) {
    var _cartTimer = null;
    new MutationObserver(function () {
      clearTimeout(_cartTimer);
      _cartTimer = setTimeout(refreshHeaderBadge, 150);
    }).observe(cartContent, { childList: true });
  }
}());

// ── Cart scroll lock (mobile) ──────────────────────────────────────────────
// On mobile, prevent the background from scrolling while the cart drawer is
// open. Uses position:fixed on body (the only reliable cross-browser approach
// for iOS Safari). Desktop allows background scrolling — standard for panels.
(function () {
  var cartEl = document.getElementById('theme-ajax-cart');
  if (!cartEl) return;
  var savedScrollY = 0;

  function lockScroll() {
    if (window.innerWidth > 768) return;
    savedScrollY = window.scrollY;
    document.body.style.top = '-' + savedScrollY + 'px';
    document.body.classList.add('cart-is-open');
  }

  function unlockScroll() {
    if (!document.body.classList.contains('cart-is-open')) return;
    document.body.classList.remove('cart-is-open');
    document.body.style.top = '';
    window.scrollTo(0, savedScrollY);
  }

  new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      if (m.attributeName === 'class') {
        cartEl.classList.contains('is-visible') ? lockScroll() : unlockScroll();
      }
    });
  }).observe(cartEl, { attributes: true, attributeFilter: ['class'] });
}());

// ── Scroll-aware sticky header (mic-ios / mic-android PDPs only) ──────────
// Hides the header when scrolling down, reveals it on scroll up.
// Targets the shopify-section wrapper (not #header) because #header's parent
// div has height === header height, which traps position:sticky with no slack.
(function () {
  var hasPDP = document.querySelector('.product-a-right, .product-b-right, .product-android-right');
  if (!hasPDP) return;

  var header = document.getElementById('header');
  if (!header) return;

  var section = header.closest('.shopify-section');
  if (!section) return;

  section.classList.add('header-section-auto-hide');

  var lastScrollY = window.scrollY;
  var ticking = false;

  window.addEventListener('scroll', function () {
    if (ticking) return;
    window.requestAnimationFrame(function () {
      var currentScrollY = window.scrollY;
      var sectionH = section.offsetHeight;

      if (currentScrollY < sectionH) {
        // Near the top — always show
        section.classList.remove('is-scrolled-down');
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down — hide
        section.classList.add('is-scrolled-down');
      } else {
        // Scrolling up — reveal
        section.classList.remove('is-scrolled-down');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    });
    ticking = true;
  }, { passive: true });
}());

// ============================================================
// Scroll-depth milestone tracker
// Captures to PostHog (primary) + fires hj('event', 'milestone_<name>')
// and a matching GTM dataLayer event when the user reaches an element with
// [data-scroll-milestone]. Each milestone fires at most once per load.
//
// Two triggers, whichever happens first:
//   1. Depth-reached (primary): the section's TOP edge has risen above the
//      viewport center — i.e. the user has scrolled the section up to mid-
//      screen. Fires INSTANTLY, no dwell, so it's velocity-independent: a fast
//      flick trips it like a slow scroll. Because deeper sections' tops cross
//      later, milestones fire in strict scroll order (a monotonic funnel).
//      Using the TOP (not the center) makes tall pinned scrollytelling sections
//      fire at stage-entry, not halfway through the pinned animation.
//   2. Dwell (fallback): the section stays "in view" (straddling the viewport
//      center OR covering >=50% of it) for 500ms continuously. Covers cases
//      trigger 1 can't — e.g. the last section, whose top may never rise
//      above mid-screen because the page can't scroll far enough.
//
// Trigger 1 replaces the old dwell-ONLY rule, which silently dropped sections
// the user flicked through faster than (in-view band / 500ms) — ~2000 px/s for
// a ~1000px-tall section, routinely exceeded by mobile flicks through the deep
// middle of a long page (caused under-firing + non-monotonic inversions).
// ============================================================
(function () {
  if (typeof window === 'undefined') return;

  var DWELL_MS = 500;
  var IN_VIEW_FRACTION = 0.5; // fraction of viewport height that counts as "in view"

  function fire(name) {
    var eventName = 'milestone_' + name;
    // PostHog — primary analysis sink. send_feature_flags attaches the
    // user's active experiment variant ($feature/<key>) to the event so
    // funnels/trends can break down scroll depth by variant natively.
    try {
      if (typeof window.posthog !== 'undefined' && typeof window.posthog.capture === 'function') {
        window.posthog.capture(eventName, { milestone: name }, { send_feature_flags: true });
      }
    } catch (e) { /* swallow */ }
    // Hotjar — dormant unless the Hotjar plan enables custom events.
    try {
      if (typeof window.hj === 'function') {
        window.hj('event', eventName);
      }
    } catch (e) { /* swallow */ }
    // GTM dataLayer — for GA4 or any other GTM-forwarded destination.
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, milestone: name });
    } catch (e) { /* swallow */ }
  }

  // Each tracked element: { el, name, timer }
  var tracked = [];

  function isInView(el) {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var r = el.getBoundingClientRect();
    if (r.height === 0) return false; // hidden (e.g. Android banner on non-Android)
    var centerY = vh / 2;
    var straddlesCenter = r.top <= centerY && r.bottom >= centerY;
    var visiblePx = Math.min(r.bottom, vh) - Math.max(r.top, 0);
    // Cover half the viewport, or — for short sections near the page edges
    // that can never reach the center — be at least half their own height on screen.
    var coversEnough = visiblePx > 0 &&
      visiblePx >= Math.min(r.height, vh * IN_VIEW_FRACTION);
    return straddlesCenter || coversEnough;
  }

  // Depth-reached trigger: the section's TOP edge has risen above the viewport
  // center, i.e. the user has scrolled the section up to mid-screen. A one-time
  // geometric crossing — fires regardless of scroll velocity. Using the top
  // (not the center) makes tall pinned sections fire at stage-entry rather than
  // halfway through their pinned scroll.
  function hasReachedDepth(el) {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var r = el.getBoundingClientRect();
    if (r.height === 0) return false; // hidden (e.g. Android banner on non-Android)
    return r.top <= vh / 2;
  }

  function evaluate() {
    for (var i = tracked.length - 1; i >= 0; i--) {
      var t = tracked[i];
      // Trigger 1: depth reached — fire immediately, no dwell (velocity-independent).
      if (hasReachedDepth(t.el)) {
        fire(t.name);
        remove(t);
        continue;
      }
      // Trigger 2: dwell fallback — section held in view for DWELL_MS continuously.
      if (isInView(t.el)) {
        if (!t.timer) {
          t.timer = window.setTimeout((function (entry) {
            return function () {
              fire(entry.name);
              remove(entry);
            };
          })(t), DWELL_MS);
        }
      } else if (t.timer) {
        window.clearTimeout(t.timer);
        t.timer = null;
      }
    }
  }

  function remove(entry) {
    if (entry.timer) { window.clearTimeout(entry.timer); entry.timer = null; }
    var idx = tracked.indexOf(entry);
    if (idx !== -1) tracked.splice(idx, 1);
    if (!tracked.length) teardown();
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      evaluate();
      ticking = false;
    });
  }

  function teardown() {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
  }

  function init() {
    var nodes = document.querySelectorAll('[data-scroll-milestone]');
    for (var i = 0; i < nodes.length; i++) {
      tracked.push({ el: nodes[i], name: nodes[i].getAttribute('data-scroll-milestone'), timer: null });
    }
    if (!tracked.length) return;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    evaluate(); // fire for whatever is already centered at load (e.g. hero)
  }

  // Start once, guarded so the homepage redirect experiment can trigger it
  // explicitly without double-initializing.
  var started = false;
  window.__audigoStartMilestones = function () {
    if (started) return;
    started = true;
    init();
  };

  // On the homepage, theme.liquid's shop-button experiment sets
  // window.__audigoHomepageExpPending while it decides whether to redirect a
  // 'test' user to /pages/homepage. Hold off until it releases us (control /
  // failsafe call __audigoStartMilestones) so test users never fire home_*
  // milestones on / before the redirect. Test users redirect away and start
  // the tracker fresh on the clone instead.
  function maybeStart() {
    if (window.__audigoHomepageExpPending) return;
    window.__audigoStartMilestones();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeStart);
  } else {
    maybeStart();
  }
}());

// ============================================================
// How-to-record video play tracking (PostHog)
// The "How to record a song with Audigo" Vimeo video (id 693788448) shows on the
// PDP — in the buy box on the information-dense variant (?view=details) and in a
// section on the control PDP. Wire the Vimeo Player API to fire play / quartile /
// completion events so video engagement is comparable across the A/B experiment.
// Events carry the active flag variant via send_feature_flags. Scroll-into-view is
// tracked separately by `milestone_pdp_video`.
// ============================================================
(function () {
  function start() {
    if (location.pathname.indexOf('/products/') === -1) return;
    var iframe = document.querySelector(
      'iframe[src*="player.vimeo.com/video/693788448"], iframe[src*="vimeo.com/video/693788448"]'
    );
    if (!iframe) return;

    function cap(name) {
      try {
        if (window.posthog && typeof posthog.capture === 'function') {
          posthog.capture(name, {}, { send_feature_flags: true });
        }
      } catch (e) {}
    }

    function init() {
      if (!window.Vimeo || !window.Vimeo.Player) return;
      var player;
      try { player = new Vimeo.Player(iframe); } catch (e) { return; }
      var fired = {};
      function once(name) { if (fired[name]) { return; } fired[name] = true; cap(name); }
      player.on('play', function () { once('pdp_video_play'); });
      player.on('timeupdate', function (data) {
        var p = (data && data.percent) || 0;
        if (p >= 0.25) { once('pdp_video_25'); }
        if (p >= 0.50) { once('pdp_video_50'); }
        if (p >= 0.75) { once('pdp_video_75'); }
      });
      player.on('ended', function () { once('pdp_video_complete'); });
    }

    if (window.Vimeo && window.Vimeo.Player) {
      init();
    } else {
      var s = document.createElement('script');
      s.src = 'https://player.vimeo.com/api/player.js';
      s.async = true;
      s.onload = init;
      document.head.appendChild(s);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}());
